// ============================================================================
// TOURNAMENT TRAINING - challenge definitions
// ============================================================================
// A challenge is a themed gauntlet of tournament decision points. Pass the
// threshold to earn PSS Coins (credited server-side with the standard 1x
// playthrough via /claimTournamentChallenge). The harder the tier, the
// bigger the reward.
//
// Curriculum grounded in public MTT training doctrine (bencb/RaiseYourEdge,
// Upswing, PokerCoaching public material):
//   - Short-stack play is NOT just shove/fold: min-raise/fold lines exist
//     down to ~7bb and exploit bubble fear.
//   - Below 10bb, push/fold discipline vs Nash-style ranges.
//   - Under 30-40bb every uncontested steal from CO/BTN matters.
//   - Big blind defense: wide vs late-position min-opens, tight vs UTG.
//   - ICM: chip-EV-marginal calls are ICM losses on the bubble; mid-stacks
//     must tighten most, big stacks apply the pressure.
// ============================================================================

export type ChallengeTier = 'bronze' | 'silver' | 'gold' | 'diamond';
export type ChallengeKind = 'gauntlet' | 'simulation';

export interface StepCard { rank: string; suit: 'hearts' | 'diamonds' | 'clubs' | 'spades'; }

export interface StepOption {
  action: string;                       // label shown on the button
  evaluation: 'best' | 'ok' | 'bad';    // best = full credit, ok = half, bad = miss
  explanation: string;                  // shown after answering
}

export interface ChallengeStep {
  id: string;
  stage: string;                        // e.g. "Middle stage · 60/120 (15)"
  heroBB: number;                       // effective stack in BB
  position: string;                     // BTN / CO / SB / BB / UTG / MP
  hand: [StepCard, StepCard];
  situation: string;                    // the setup narrative
  actionTo: string;                     // "Action folds to you." etc.
  options: StepOption[];
  keyLesson: string;
}

export type SimulationGoalType =
  | 'chips'        // reach goalChips within maxHands
  | 'preflopWins'  // win goalCount pots BEFORE the flop within maxHands (steal drill)
  | 'survive';     // finish maxHands with at least goalChips (don't bust, don't bleed)

export interface SimulationConfig {
  smallBlind: number;
  bigBlind: number;
  heroChips: number;      // your starting stack (the handicap IS the challenge)
  botChips: number;       // each bot's starting stack
  botCount: number;
  botDifficulty: 'easy' | 'medium' | 'hard' | 'mixed';
  goalType: SimulationGoalType;
  goalChips: number;      // chips target ('chips'/'survive')
  goalCount?: number;     // e.g. preflop pots to steal ('preflopWins')
  maxHands: number;
  mission: string;        // one-line mission statement shown in the briefing + HUD
  tips: string[];         // 3-4 strategy tips shown in the pre-game briefing
}

export interface TournamentChallenge {
  id: string;
  title: string;
  tagline: string;
  description: string;
  tier: ChallengeTier;
  rewardPkCash: number;
  kind: ChallengeKind;
  concept: string;
  passThreshold: number;                // minimum "best" answers ("ok" counts half)
  steps: ChallengeStep[];
  simulation?: SimulationConfig;        // kind === 'simulation': live mini-game config
  comingSoon?: boolean;
  // 🏆 PSS PRO SERIES: exclusive scenarios authored from the real hands of a
  // verified top-ranked tournament pro (five-figure winnings, 20+ final
  // tables). These are the decisions that made actual money - not chart
  // theory.
  proSeries?: boolean;
}

export const PRO_SERIES_BLURB =
  'Exclusive PSS scenarios authored from the real hands of a verified '
  + 'top-ranked tournament pro - five-figure tournament winnings, 20+ final '
  + 'tables. These are the exact decisions that made real money.';

export const TIER_META: Record<ChallengeTier, { label: string; color: string; order: number }> = {
  bronze:  { label: 'Bronze',  color: '#b45309', order: 1 },
  silver:  { label: 'Silver',  color: '#94a3b8', order: 2 },
  gold:    { label: 'Gold',    color: '#f59e0b', order: 3 },
  diamond: { label: 'Diamond', color: '#22d3ee', order: 4 },
};

// The tournament skill each concept trains - shown on every challenge card
// and mission briefing so players always know WHAT they're improving.
export const SKILL_LABELS: Record<string, string> = {
  'short-stack':    'Short-Stack Play & Shove/Fold Discipline',
  'steal-resteal':  'Stealing Blinds & Late-Position Aggression',
  'tournament-icm': 'ICM, Bubble Play & Pay-Jump Decisions',
  'preflop-ranges': 'Preflop Ranges & Hand Selection',
  'position':       'Positional Awareness',
};

export const getSkillLabel = (concept: string): string =>
  SKILL_LABELS[concept] || 'Tournament Fundamentals';

// Key glossary terms per concept - shown inline in the mission briefing so
// a beginner never has to guess what "steal" or "jam" means mid-challenge.
// Keys reference POKER_GLOSSARY entries in trainingAnalysisService.
export const CONCEPT_GLOSSARY_KEYS: Record<string, string[]> = {
  'steal-resteal':  ['steal', 'rejam', 'fold_equity', 'dead_money'],
  'short-stack':    ['jam', 'push_fold', 'effective_stack', 'fold_equity'],
  'tournament-icm': ['icm', 'bubble', 'pay_jump', 'chip_leader'],
  'preflop-ranges': ['blinds', 'ante', 'steal', 'blocker'],
  'position':       ['position', 'button', 'late_position', 'steal'],
};

const c = (rank: string, suit: StepCard['suit']): StepCard => ({ rank, suit });

// ============================================================================
// BRONZE - Steal School (late-position aggression fundamentals)
// ============================================================================
const STEAL_SCHOOL: TournamentChallenge = {
  id: 'steal-school',
  title: 'Steal School',
  tagline: 'Late position is a profit center',
  description: 'Under 40bb every uncontested steal meaningfully grows your stack. Learn which hands to open from the CO and BTN, and when folding is fine.',
  tier: 'bronze',
  rewardPkCash: 0.5,
  kind: 'gauntlet',
  concept: 'steal-resteal',
  passThreshold: 6,
  steps: [
    {
      id: 'ss-1', stage: 'Middle stage · 100/200 (25)', heroBB: 28, position: 'BTN',
      hand: [c('9', 'spades'), c('7', 'spades')],
      situation: 'Folds to you on the button. Blinds are tight regulars who fold too much.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Min-raise', evaluation: 'best', explanation: 'Textbook steal. 97s has playability when called, and against tight blinds a min-raise shows immediate profit even winning it preflop under half the time.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Too tight on the button vs tight blinds. You are burning the most profitable seat at the table.' },
        { action: 'Limp', evaluation: 'bad', explanation: 'Limping invites the blinds in cheaply and gives up the initiative that makes steals profitable.' },
      ],
      keyLesson: 'BTN vs tight blinds is the widest profitable opening spot in poker.',
    },
    {
      id: 'ss-2', stage: 'Middle stage · 100/200 (25)', heroBB: 28, position: 'CO',
      hand: [c('J', 'diamonds'), c('4', 'clubs')],
      situation: 'Folds to you in the cutoff. The button is a loose aggressive player who 3-bets steals relentlessly.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'J4o is well below a CO open, and the aggressive button behind makes marginal opens worse - you will face 3-bets you cannot continue against.' },
        { action: 'Min-raise', evaluation: 'bad', explanation: 'J4o is a losing open from the CO even before accounting for the 3-bettor behind you.' },
        { action: 'Open big (3bb)', evaluation: 'bad', explanation: 'Sizing up with trash just loses more when the button plays back.' },
      ],
      keyLesson: 'Steal wide, not with anything - and tighten up when aggression sits behind you.',
    },
    {
      id: 'ss-3', stage: 'Middle stage · 150/300 (40)', heroBB: 22, position: 'BTN',
      hand: [c('A', 'hearts'), c('5', 'hearts')],
      situation: 'Folds to you. The small blind is a short 9bb stack who has been shoving over steals.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Min-raise, call a shove', evaluation: 'best', explanation: 'A5s is a clear open and comfortably ahead of a 9bb resteal range. Raise planning to call the jam.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'A5s on the button is far too strong to fold, even expecting action.' },
        { action: 'Min-raise, fold to a shove', evaluation: 'ok', explanation: 'The open is right, but folding A5s getting ~2:1 against a wide 9bb jamming range gives up too much equity.' },
      ],
      keyLesson: 'Steal with a plan: know before you raise whether you call the resteal.',
    },
    {
      id: 'ss-4', stage: 'Middle stage · 150/300 (40)', heroBB: 35, position: 'SB',
      hand: [c('K', 'clubs'), c('8', 'diamonds')],
      situation: 'Folds to you in the small blind. Big blind is a solid regular who defends properly.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Raise 3x', evaluation: 'best', explanation: 'SB vs BB you must attack, and a larger size denies the BB the great price a min-raise offers. K8o is a standard SB open.' },
        { action: 'Limp', evaluation: 'ok', explanation: 'Limping is playable in SB vs BB battles, but vs a solid defender the raise takes the pot outright far more often.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding K8o in the small blind blind-vs-blind is far too tight.' },
      ],
      keyLesson: 'Blind-vs-blind, the aggressor usually wins the war of wide ranges.',
    },
    {
      id: 'ss-5', stage: 'Late stage · 300/600 (75)', heroBB: 18, position: 'CO',
      hand: [c('Q', 'spades'), c('10', 'spades')],
      situation: 'Folds to you at 18bb with antes in play. Both blinds cover you slightly.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Min-raise', evaluation: 'best', explanation: 'QTs is a clear open at this depth, and min-raising keeps your fold option alive against jams while attacking the dead money.' },
        { action: 'Shove 18bb', evaluation: 'bad', explanation: 'Open-jamming 18bb with a hand this strong risks a full stack to win the blinds - min-raise gets the same folds cheaper.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'With antes sweetening the pot, folding QTs in the CO leaks steady equity.' },
      ],
      keyLesson: 'Antes make stealing mandatory - and min-raising preserves your stack flexibility.',
    },
    {
      id: 'ss-6', stage: 'Middle stage · 200/400 (50)', heroBB: 32, position: 'BTN',
      hand: [c('8', 'diamonds'), c('6', 'diamonds')],
      situation: 'Folds to you. The BB has defended his last five big blinds and check-raises flops aggressively.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Min-raise', evaluation: 'best', explanation: '86s stays an open even vs a fighter - it flops well, and you keep position all hand. The answer to sticky defenders is better postflop discipline, not surrendering the button.' },
        { action: 'Fold', evaluation: 'ok', explanation: 'Defensible at the bottom of the range versus a station, but habitually releasing the button trains the table to attack you.' },
        { action: 'Open 3.5x', evaluation: 'bad', explanation: 'Sizing up into a player who will not fold just builds a bigger pot out of your control.' },
      ],
      keyLesson: 'Sticky defenders change your postflop plan, not your opening range.',
    },
    {
      id: 'ss-7', stage: 'Late stage · 300/600 (75)', heroBB: 24, position: 'BTN',
      hand: [c('10', 'clubs'), c('8', 'clubs')],
      situation: 'You min-raise T8s, and the 26bb big blind calls. Flop K72 rainbow. He checks.',
      actionTo: 'Action on you.',
      options: [
        { action: 'C-bet small (1/3 pot)', evaluation: 'best', explanation: 'Dry king-high boards favor the raiser massively. A small stab folds out his ace-highs and gutters immediately, and costs little when he continues.' },
        { action: 'Check back', evaluation: 'ok', explanation: 'Fine sometimes, but on a board this good for your range, checking back all your air leaves steady money unclaimed.' },
        { action: 'Bet pot', evaluation: 'bad', explanation: 'Oversizing a bluff on a board where a small bet gets the same folds is pure waste.' },
      ],
      keyLesson: 'The steal is not over at the flop - dry high boards are cheap, high-frequency stab spots.',
    },
    {
      id: 'ss-8', stage: 'Late stage · 400/800 (100)', heroBB: 20, position: 'SB',
      hand: [c('Q', 'clubs'), c('5', 'diamonds')],
      situation: 'Folds to you. The BB is a 10bb stack who has rejammed his last two steal attempts.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Q5o cannot profitably raise/call versus a proven rejammer, and raise/folding burns 2bb+ each time he shoves. Against him, tighten your steals to hands that welcome the jam.' },
        { action: 'Raise, fold to jam', evaluation: 'bad', explanation: 'He has shown you his counter twice - opening trash into a rejammer is donating.' },
        { action: 'Limp', evaluation: 'ok', explanation: 'A limp keeps his jam threat smaller, but Q5o out of position still plays poorly - folding is cleaner.' },
      ],
      keyLesson: 'Steal frequencies must respond to who fights back - rejammers turn your bluffs into folds.',
    },
  ],
};

// ============================================================================
// SILVER - Push/Fold Precision (sub-10bb discipline)
// ============================================================================
const PUSH_FOLD: TournamentChallenge = {
  id: 'push-fold-precision',
  title: 'Push/Fold Precision',
  tagline: 'Master the sub-10bb decision tree',
  description: 'Below 10 big blinds most decisions collapse to all-in or fold. Drilling Nash-style shove ranges turns the scariest stack size into a weapon.',
  tier: 'silver',
  rewardPkCash: 1.0,
  kind: 'gauntlet',
  concept: 'short-stack',
  passThreshold: 8,
  steps: [
    {
      id: 'pf-1', stage: 'Late stage · 500/1000 (125)', heroBB: 8, position: 'BTN',
      hand: [c('K', 'diamonds'), c('7', 'clubs')],
      situation: 'Folds to you on the button at 8bb.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'K7o is comfortably inside an 8bb button jamming range - you have blockers, fold equity, and live cards when called.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Far too tight. Waiting for premiums at 8bb blinds you off before they arrive.' },
        { action: 'Min-raise', evaluation: 'ok', explanation: 'A min-raise/fold line exists here, but K7o plays better as a jam - you deny the blinds their equity entirely.' },
      ],
      keyLesson: 'At 8bb on the button, jam any king - fold equity plus live cards is enough.',
    },
    {
      id: 'pf-2', stage: 'Late stage · 500/1000 (125)', heroBB: 8, position: 'UTG',
      hand: [c('K', 'diamonds'), c('7', 'clubs')],
      situation: 'You are first to act at a full 8-handed table, 8bb deep.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Same hand, different world: with seven players behind, K7o runs into a caller far too often. UTG jamming ranges stay tight even short.' },
        { action: 'All-in', evaluation: 'bad', explanation: 'K7o UTG at a full table is a losing jam - too many hands behind you dominate it.' },
        { action: 'Min-raise', evaluation: 'bad', explanation: 'Min-raise/folding K7o UTG just burns chips - if the hand is not worth jamming here, it is not worth playing.' },
      ],
      keyLesson: 'Position transforms shove ranges: the same hand is a snap-jam BTN and a snap-fold UTG.',
    },
    {
      id: 'pf-3', stage: 'Late stage · 600/1200 (150)', heroBB: 9, position: 'SB',
      hand: [c('J', 'hearts'), c('3', 'hearts')],
      situation: 'Folds to you in the small blind. The big blind is a tight player protecting a 25bb stack.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'SB vs BB at 9bb, J3s is a profitable jam against a tight caller - his folding frequency alone pays for it.' },
        { action: 'Fold', evaluation: 'ok', explanation: 'Not a disaster, but versus a tight defender you are passing on free blinds and antes you badly need.' },
        { action: 'Limp', evaluation: 'bad', explanation: 'Limping 9bb in the SB invites a raise you cannot handle and plays your weak hand out of position postflop.' },
      ],
      keyLesson: 'Blind-vs-blind shoves print money against tight defenders - suited junk qualifies.',
    },
    {
      id: 'pf-4', stage: 'Late stage · 600/1200 (150)', heroBB: 7, position: 'BB',
      hand: [c('A', 'clubs'), c('8', 'diamonds')],
      situation: 'A 30bb cutoff min-raises. Folds to you in the big blind at 7bb.',
      actionTo: 'Action on you: call 1bb more, jam, or fold.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'A8o at 7bb over a late-position min-raise is a mandatory jam - ahead of his stealing range, and flatting leaves you 6bb with no plan.' },
        { action: 'Call', evaluation: 'bad', explanation: 'Calling off 15% of your stack to play A8o out of position postflop wastes the hand\'s jam equity.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding an ace this strong at 7bb versus a wide steal is far too tight.' },
      ],
      keyLesson: 'Short in the BB versus a steal: rejam or fold - flatting is the one wrong answer.',
    },
    {
      id: 'pf-5', stage: 'Late stage · 800/1600 (200)', heroBB: 7, position: 'BTN',
      hand: [c('6', 'spades'), c('6', 'clubs')],
      situation: 'A tight UTG player open-jams for 9bb. Folds to you on the button with 7bb.',
      actionTo: 'Action on you: call all-in or fold.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Small pairs are flips at best against a tight UTG jamming range, and often crushed by bigger pairs. Calling off your tournament on a coin-flip you can decline is a leak.' },
        { action: 'Call all-in', evaluation: 'bad', explanation: '66 versus tight UTG jams is barely a flip and sometimes dominated - you find clearly better spots with 7bb and a button full of jam opportunities.' },
      ],
      keyLesson: 'Having a jamming hand does not mean having a calling hand - calling ranges stay far tighter.',
    },
    {
      id: 'pf-6', stage: 'Late stage · 800/1600 (200)', heroBB: 7, position: 'CO',
      hand: [c('Q', 'hearts'), c('J', 'hearts')],
      situation: 'Folds to you in the cutoff at 7bb. The button is an aggressive big stack.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Min-raise, call a shove', evaluation: 'best', explanation: 'The bencb line: even at 7bb you can min-raise/call with your strongest hands. It induces jams from worse and keeps his bluffs in - jamming QJs yourself folds them out.' },
        { action: 'All-in', evaluation: 'ok', explanation: 'Never bad with QJs, but the open-jam folds out every hand you dominate. The min-raise/call line extracts more from an aggressive button.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'QJs at 7bb in the CO is far too strong to fold in any line.' },
      ],
      keyLesson: 'Short-stack play is not only shove/fold - min-raising your best hands invites dominated aggression.',
    },
    {
      id: 'pf-7', stage: 'Late stage · 1000/2000 (250)', heroBB: 5, position: 'MP',
      hand: [c('A', 'diamonds'), c('2', 'clubs')],
      situation: 'Folds to you at 5bb, six players left at the table.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'At 5bb any ace jams from almost anywhere - the blocker effect alone (an opponent needs a bigger ace or a pair to call) carries the play.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'A2o at 5bb is well inside the jamming range; folding here just guarantees you jam something worse next orbit.' },
        { action: 'Min-raise', evaluation: 'bad', explanation: 'At 5bb a min-raise commits a third of your stack with no fold option - jam and take the full fold equity.' },
      ],
      keyLesson: 'Below 6bb, any ace is a shove from most seats - the blocker does the heavy lifting.',
    },
    {
      id: 'pf-8', stage: 'Late stage · 1000/2000 (250)', heroBB: 12, position: 'BB',
      hand: [c('K', 'spades'), c('10', 'diamonds')],
      situation: 'A 6bb UTG stack open-jams. Folds to you in the BB.',
      actionTo: 'Action on you: call 6bb or fold.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'Getting better than 2.2:1 against a desperate 6bb UTG jam, KTo is a clear call - his range at that stack is wide enough that you are flipping or better.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'The price is too good. Folding hands this strong versus micro-stack jams slowly bleeds you.' },
      ],
      keyLesson: 'Calling ranges scale with the jammer\'s desperation - 6bb jams get looked up light.',
    },
    {
      id: 'pf-9', stage: 'Late stage · 1200/2400 (300)', heroBB: 9, position: 'CO',
      hand: [c('9', 'hearts'), c('8', 'hearts')],
      situation: 'Folds to you at 9bb. Behind you: a loose button and two medium-stack blinds.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: '98s at 9bb in the CO is a standard jam: live, connected, suited - it plays terribly as a raise/fold and beautifully as a shove.' },
        { action: 'Fold', evaluation: 'ok', explanation: 'Borderline defensible with a loose caller behind, but tossing the top of your suited-connector range gives up too much.' },
        { action: 'Limp', evaluation: 'bad', explanation: 'A 9bb CO limp announces weakness and invites a raise that forces you off your equity.' },
      ],
      keyLesson: 'Suited connectors keep their jam value - they are never dominated the way weak offsuit hands are.',
    },
    {
      id: 'pf-10', stage: 'Late stage · 1200/2400 (300)', heroBB: 10, position: 'SB',
      hand: [c('A', 'hearts'), c('J', 'clubs')],
      situation: 'A 22bb cutoff opens 2.2x. Button folds. You have 10bb in the SB.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'AJo at 10bb over a late open is a premium rejam - well ahead of his continuing range, and his fold frequency adds the rest.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding AJo to one late open at rejam depth is a fear fold, not a strategy.' },
        { action: 'Call', evaluation: 'bad', explanation: 'Flatting 10bb out of position with no plan wastes the hand\'s dominant jam equity.' },
      ],
      keyLesson: 'The 8-14bb rejam is the short stack\'s best weapon - big aces are its core.',
    },
  ],
};

// ============================================================================
// SILVER - Big Blind Fortress (defense fundamentals)
// ============================================================================
const BB_FORTRESS: TournamentChallenge = {
  id: 'bb-fortress',
  title: 'Big Blind Fortress',
  tagline: 'Stop burning money from the blinds',
  description: 'With antes in play you are getting a huge price to defend. Learn what to peel, what to rejam, and what to release.',
  tier: 'silver',
  rewardPkCash: 1.0,
  kind: 'gauntlet',
  concept: 'preflop-ranges',
  passThreshold: 6,
  steps: [
    {
      id: 'bf-1', stage: 'Middle stage · 200/400 (50)', heroBB: 30, position: 'BB',
      hand: [c('9', 'hearts'), c('6', 'hearts')],
      situation: 'The button min-raises. Small blind folds.',
      actionTo: 'Action on you: 1bb more to call.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'Getting better than 3.5:1 with antes, suited connectors and gappers are automatic defends against a button min-raise.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding hands this playable at this price is exactly the leak that bleeds stacks from the big blind.' },
        { action: '3-bet', evaluation: 'ok', explanation: 'A fine occasional bluff, but 96s performs better realizing its equity cheaply in position-closing action.' },
      ],
      keyLesson: 'With antes, the BB price makes almost every suited hand a defend vs late opens.',
    },
    {
      id: 'bf-2', stage: 'Middle stage · 200/400 (50)', heroBB: 30, position: 'BB',
      hand: [c('9', 'hearts'), c('6', 'hearts')],
      situation: 'A tight UTG player raises 2.5x at a full table. Everyone folds to you.',
      actionTo: 'Action on you: 1.5bb more to call.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Same hand, wrong opponent: versus a tight UTG range you are dominated or drawing thin, and the bigger sizing worsens your price.' },
        { action: 'Call', evaluation: 'bad', explanation: '96s plays terribly against a strong early-position range - your flush draws lose to bigger ones and pairs are rarely best.' },
        { action: '3-bet', evaluation: 'bad', explanation: 'Bluffing into the tightest range at the table is lighting chips on fire.' },
      ],
      keyLesson: 'Defense is opponent-relative: wide versus BTN steals, tight versus UTG strength.',
    },
    {
      id: 'bf-3', stage: 'Late stage · 400/800 (100)', heroBB: 14, position: 'BB',
      hand: [c('K', 'spades'), c('9', 'spades')],
      situation: 'The cutoff (35bb) min-raises. Folds to you at 14bb.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'K9s at 14bb versus a late steal is a premium rejam: strong blockers, real equity when called, and massive fold equity against a wide range.' },
        { action: 'Call', evaluation: 'ok', explanation: 'Playable, but flatting leaves you guessing on most flops - the jam maximizes your two forms of equity now.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Way too tight against a cutoff steal at a rejam-perfect stack depth.' },
      ],
      keyLesson: '12-16bb is prime rejam territory: strong suited broadways punish wide steals.',
    },
    {
      id: 'bf-4', stage: 'Late stage · 400/800 (100)', heroBB: 25, position: 'BB',
      hand: [c('7', 'diamonds'), c('2', 'clubs')],
      situation: 'The small blind completes (calls). You have the option.',
      actionTo: 'Action on you: check or raise.',
      options: [
        { action: 'Check', evaluation: 'best', explanation: 'You close the action with a free flop holding the worst hand in poker. Take it - even 72o flops trips sometimes, for free.' },
        { action: 'Raise', evaluation: 'bad', explanation: 'Bluff-raising limps with no equity and no plan turns a free flop into a built pot you do not want.' },
      ],
      keyLesson: 'Never pay extra with trash, never decline a free flop.',
    },
    {
      id: 'bf-5', stage: 'Late stage · 500/1000 (125)', heroBB: 20, position: 'BB',
      hand: [c('A', 'diamonds'), c('J', 'clubs')],
      situation: 'An aggressive button (covers you) raises 2.2x. He has raised 6 of the last 8 buttons.',
      actionTo: 'Action on you.',
      options: [
        { action: '3-bet to 7bb', evaluation: 'best', explanation: 'AJo crushes a 75% button-raising frequency. 3-betting extracts value from his continues and denies his junk its equity - flatting lets him realize it.' },
        { action: 'Call', evaluation: 'ok', explanation: 'Not terrible, but versus this open frequency AJo is a value hand - play it like one.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding AJo to a serial stealer is surrender.' },
      ],
      keyLesson: 'Versus relentless stealers, your good hands move up a value tier - punish, don\'t just defend.',
    },
    {
      id: 'bf-6', stage: 'Middle stage · 300/600 (75)', heroBB: 26, position: 'BB',
      hand: [c('10', 'diamonds'), c('9', 'diamonds')],
      situation: 'You defended T9s vs a button min-raise. Flop 8d 7c 2h giving you an open-ender. He c-bets 1/3 pot.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Check-raise', evaluation: 'best', explanation: 'Open-enders on low boards are premium check-raise candidates: you fold out his overcards now and hit eight clean outs when called - two ways to win.' },
        { action: 'Call', evaluation: 'ok', explanation: 'Never wrong with this equity, but flat-calling lets his ace-high bluffs realize free equity against you.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding eight outs plus backdoors to a small stab is defending the blind just to give up.' },
      ],
      keyLesson: 'Defense earns its money postflop - big draws on low boards should attack, not call.',
    },
    {
      id: 'bf-7', stage: 'Late stage · 500/1000 (125)', heroBB: 16, position: 'BB',
      hand: [c('Q', 'clubs'), c('6', 'spades')],
      situation: 'The 40bb chip-leading button min-raises for the fourth orbit running. You are at 16bb.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Q6o at 16bb is the wrong hand to fight with: too weak to rejam profitably into a stack that snap-calls wide, too dominated to defend. Pick a better sword.' },
        { action: 'All-in', evaluation: 'bad', explanation: 'Rejamming trash into the one player who can and will call you off is how 16bb becomes 0.' },
        { action: 'Call', evaluation: 'ok', explanation: 'The price is decent, but out of position with a dominated hand versus the table bully, most flops play themselves badly.' },
      ],
      keyLesson: 'Getting run over is not fixed by fighting with junk - wait one orbit for a hand that fights well.',
    },
    {
      id: 'bf-8', stage: 'Late stage · 600/1200 (150)', heroBB: 22, position: 'BB',
      hand: [c('J', 'hearts'), c('10', 'hearts')],
      situation: 'A 30bb MP opens 2.2x, the 25bb button flat-calls. You are in the BB with JTs.',
      actionTo: 'Action on you: 1.4bb more to call into a 6.4bb pot.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'Nearly 5:1 closing the action with one of the best multiway hands in poker - JTs makes straights and flushes that stack people. Automatic.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding JTs getting almost 5:1 closing the action is throwing away one of the clearest calls in tournaments.' },
        { action: 'Squeeze all-in', evaluation: 'bad', explanation: 'Jamming 22bb into an opener AND a caller with a drawing hand torches its multiway playability for thin fold equity.' },
      ],
      keyLesson: 'Multiway with position closed and a huge price, suited connectors and broadways are automatic defends.',
    },
  ],
};

// ============================================================================
// GOLD - Bubble Tightrope (ICM pressure decisions)
// ============================================================================
const BUBBLE_TIGHTROPE: TournamentChallenge = {
  id: 'bubble-tightrope',
  title: 'Bubble Tightrope',
  tagline: 'Chip EV lies to you on the bubble',
  description: 'A marginally profitable chip-EV call is almost always an ICM disaster near the money. Learn when to tighten, when to pressure, and who should be doing which.',
  tier: 'gold',
  rewardPkCash: 2.0,
  kind: 'gauntlet',
  concept: 'tournament-icm',
  passThreshold: 6,
  steps: [
    {
      id: 'bt-1', stage: 'Bubble · 1000/2000 (250) · 46 left, 45 paid', heroBB: 18, position: 'BB',
      hand: [c('A', 'clubs'), c('Q', 'diamonds')],
      situation: 'The chip leader (80bb) open-jams from the SB, covering you. Three 4bb stacks sit at other tables.',
      actionTo: 'Action on you: call 18bb or fold.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'AQo is a chip-EV call and an ICM fold. With micro-stacks about to blind out, your tournament life is worth more than the flip - the chip leader jams wide BECAUSE you cannot call.' },
        { action: 'Call all-in', evaluation: 'bad', explanation: 'Even winning the flip only doubles your chips - losing costs you a near-certain cash plus all future equity. This is the classic bubble punt.' },
      ],
      keyLesson: 'On the stone bubble, marginal chip-EV calls become clear ICM folds - especially versus the one stack that covers you.',
    },
    {
      id: 'bt-2', stage: 'Bubble · 1000/2000 (250) · 46 left, 45 paid', heroBB: 80, position: 'SB',
      hand: [c('8', 'diamonds'), c('3', 'clubs')],
      situation: 'You ARE the chip leader. Folds to you. The BB has 18bb and just folded three hands in a row.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Raise / put him all-in', evaluation: 'best', explanation: 'Flip the last hand around: ICM handcuffs HIM, not you. Your worst hands profit from relentless pressure because his calling range is brutally tight.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'The chip leader folding to a handcuffed medium stack on the bubble is refusing free money.' },
        { action: 'Limp', evaluation: 'ok', explanation: 'Pressure-light. Playable, but the raise wins immediately against a range that mostly cannot continue.' },
      ],
      keyLesson: 'ICM is a weapon for whoever covers: big stacks should attack the bubble hardest.',
    },
    {
      id: 'bt-3', stage: 'Bubble · 1200/2400 (300) · 46 left, 45 paid', heroBB: 4, position: 'BTN',
      hand: [c('K', 'hearts'), c('Q', 'hearts')],
      situation: 'You are the shortest stack in the room. Two other tables have 6bb stacks. Folds to you.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'At 4bb your fold-to-cash plan is already dead - you blind out before the money. KQs is a monster here; take the double or go out fighting.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding your way in only works when the blinds will not eat you first. At 4bb with antes, they will.' },
      ],
      keyLesson: 'Micro-stacks are freed from ICM: when folding cannot reach the money, play for chips.',
    },
    {
      id: 'bt-4', stage: 'Bubble · 1200/2400 (300) · 47 left, 45 paid', heroBB: 22, position: 'CO',
      hand: [c('J', 'clubs'), c('J', 'spades')],
      situation: 'A 20bb MP regular opens 2.2x. You cover him slightly. Behind you: a 60bb button.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'JJ is too strong to flat and too vulnerable to trap with. Jamming maximizes pressure on his ICM-squeezed continuing range - he folds everything but the top of it.' },
        { action: 'Call', evaluation: 'ok', explanation: 'Keeps his worse hands in, but invites the big-stack button to squeeze you off the best hand.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding JJ to a single open, even on the bubble, massively overcorrects.' },
      ],
      keyLesson: 'Premium pairs on the bubble: end the hand preflop while ICM makes their calling range tiny.',
    },
    {
      id: 'bt-5', stage: 'Just in the money · 1500/3000 (400) · 44 left, 45 paid', heroBB: 12, position: 'BTN',
      hand: [c('A', 'spades'), c('4', 'diamonds')],
      situation: 'The bubble just burst. Folds to you at 12bb.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'The moment the bubble bursts, ICM pressure collapses and everyone opens up - jam your wheel aces before the table recalibrates. Min-cashers gamble now.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Bubble-tight is over. Staying in fold-mode after the money burns your best accumulation window.' },
        { action: 'Min-raise', evaluation: 'ok', explanation: 'Playable, but at 12bb post-bubble the jam takes maximal advantage of the gamble-happy chaos.' },
      ],
      keyLesson: 'The seconds after the bubble bursts are the loosest of the tournament - shift gears instantly.',
    },
    {
      id: 'bt-6', stage: 'Bubble · 1500/3000 (400) · 31 left, 30 paid', heroBB: 25, position: 'MP',
      hand: [c('A', 'clubs'), c('K', 'spades')],
      situation: 'The 15bb UTG player open-jams. You cover him; behind you sit two big stacks.',
      actionTo: 'Action on you: call 15bb or fold.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'AKo versus a 15bb UTG jam is still a call on the bubble - his range is real hands, and against it AK is a favorite or flipping with excellent equity. Do not let ICM talk you out of premium spots.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'ICM tightens marginal calls, not premium ones - folding AK here overcorrects into weakness the table will exploit.' },
      ],
      keyLesson: 'Bubble discipline means folding marginal hands, not surrendering premiums.',
    },
    {
      id: 'bt-7', stage: 'Bubble · 1500/3000 (400) · 31 left, 30 paid', heroBB: 35, position: 'BTN',
      hand: [c('K', 'diamonds'), c('4', 'diamonds')],
      situation: 'Folds to you. The SB (28bb) is a strong reg; the BB (30bb) just lost a big pot and is visibly steaming.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'ok', explanation: 'Not unreasonable versus a tilted player who may spite-call - but the safer read is that even tilted players fold on the stone bubble.' },
        { action: 'Min-raise', evaluation: 'best', explanation: 'K4s on the button with bubble handcuffs on BOTH blinds is a profitable open regardless of the steamer - his continuing still requires hands, and yours has a king and a suit.' },
        { action: 'Open-jam 35bb', evaluation: 'bad', explanation: 'Risking your whole comfortable stack where a min-raise gets the same folds is a catastrophic risk/reward trade.' },
      ],
      keyLesson: 'On the bubble the math of pressure beats the psychology of tilt - but never risk more than the steal needs.',
    },
    {
      id: 'bt-8', stage: 'Bubble · 2000/4000 (500) · 31 left, 30 paid', heroBB: 8, position: 'UTG',
      hand: [c('7', 'clubs'), c('7', 'diamonds')],
      situation: 'You are at 8bb UTG. Two other tables each have a 2bb stack posting the big blind this hand.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Normally a snap-jam - but with 2bb stacks all-in by force elsewhere THIS HAND, the cash is seconds away. A flip for your tournament right now is the worst-timed gamble in poker.' },
        { action: 'All-in', evaluation: 'bad', explanation: 'Chip-EV says jam; the two forced all-ins elsewhere say your min-cash is nearly guaranteed if you simply wait one hand.' },
      ],
      keyLesson: 'Know where the micro-stacks are: when someone else is all-in by force, your clock stops.',
    },
  ],
};

// ============================================================================
// GOLD - Final Table Laddering (ICM at the end game)
// ============================================================================
const FINAL_TABLE: TournamentChallenge = {
  id: 'final-table-ladder',
  title: 'Final Table Laddering',
  tagline: 'Every elimination is a pay raise',
  description: 'Pay jumps warp every decision at the final table. Learn when surviving one more bust-out is worth more than the chips.',
  tier: 'gold',
  rewardPkCash: 2.0,
  kind: 'gauntlet',
  concept: 'tournament-icm',
  passThreshold: 5,
  steps: [
    {
      id: 'ft-1', stage: 'Final table · 5 left · next jump +$120', heroBB: 15, position: 'BB',
      hand: [c('A', 'diamonds'), c('10', 'clubs')],
      situation: 'Second-shortest with 15bb. The 8bb micro-stack is in the SB next hand. The 40bb chip leader jams from the BTN.',
      actionTo: 'Action on you: call 15bb or fold.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'ATo is ahead of his jamming range by chips - and still a fold. The 8bb stack must survive the blinds first; laddering past him is close to free money.' },
        { action: 'Call all-in', evaluation: 'bad', explanation: 'Busting 5th when 4th was nearly guaranteed is one of the costliest mistakes in tournament poker.' },
      ],
      keyLesson: 'With a micro-stack about to be blinded out, your calling ranges collapse to near-nothing.',
    },
    {
      id: 'ft-2', stage: 'Final table · 4 left · you cover everyone', heroBB: 45, position: 'BTN',
      hand: [c('Q', 'diamonds'), c('9', 'clubs')],
      situation: 'Chip leader. Both blinds sit on 12-15bb, each one pay jump from a life-changing score.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Min-raise', evaluation: 'best', explanation: 'Cover-everyone at a final table is a license to open relentlessly - their rejam thresholds are sky-high while jumps loom. Q9o prints here.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'The chip lead is for using. Passing these spots donates the ladder-pressure advantage back.' },
        { action: 'Open-jam', evaluation: 'bad', explanation: 'Jamming risks 45bb to win 2.4 - the min-raise achieves the same folds at a fraction of the exposure.' },
      ],
      keyLesson: 'The chip leader taxes the table while ICM freezes everyone else - keep the sizing small.',
    },
    {
      id: 'ft-3', stage: 'Final table · 3 left · payouts 50/30/20', heroBB: 20, position: 'SB',
      hand: [c('K', 'clubs'), c('J', 'diamonds')],
      situation: 'Three-handed, stacks nearly even (~20bb each). The BTN folds.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Raise', evaluation: 'best', explanation: 'Even stacks three-handed dilutes ICM distortion and KJo is well above average blind-vs-blind. Play for the win - the jump from 2nd to 1st is the biggest on the ladder.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'KJo blind-vs-blind three-handed is a monster. This is over-applied ICM caution.' },
        { action: 'Limp', evaluation: 'ok', explanation: 'A limping strategy exists here, but with a hand this strong the raise builds the pot you want.' },
      ],
      keyLesson: 'ICM caution peaks at pay-jump asymmetry - with even stacks, revert toward chip-EV aggression.',
    },
    {
      id: 'ft-4', stage: 'Heads up · blinds 2000/4000', heroBB: 22, position: 'SB/BTN',
      hand: [c('8', 'clubs'), c('5', 'diamonds')],
      situation: 'Heads up for the title. Your opponent (28bb) folds his SB too often and plays fit-or-fold postflop.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Min-raise', evaluation: 'best', explanation: 'Heads up, ICM is gone - pure chip battle. Versus an over-folder, any two cards profit from a min-raise; 85o included.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding the button heads up versus an over-folder gives away the match one small pot at a time.' },
        { action: 'Limp', evaluation: 'ok', explanation: 'Workable as a mixed strategy, but the raise exploits his precise leak: folding too much preflop.' },
      ],
      keyLesson: 'Heads up is chip-EV poker again - exploit the specific player, not a chart.',
    },
    {
      id: 'ft-5', stage: 'Final table · 7 left · next jump +$80', heroBB: 30, position: 'MP',
      hand: [c('A', 'hearts'), c('Q', 'spades')],
      situation: 'The 55bb chip leader opens 2.3x UTG. You are second in chips; three short stacks (8-12bb) sit behind.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'ok', explanation: 'Tight but principled: big-stack-vs-big-stack confrontations carry the worst ICM cost at a final table full of shorts.' },
        { action: 'Call', evaluation: 'best', explanation: 'AQo is too strong to fold but exactly wrong to escalate: calling keeps the pot controlled against the one player who can bust you, while the shorts stay under pressure.' },
        { action: '3-bet', evaluation: 'bad', explanation: '3-betting invites a 4-bet that forces an awful ICM decision - second place should not build big pots against first without a premium.' },
      ],
      keyLesson: 'The two biggest stacks at a short-stacked final table should avoid each other - someone else will bust first.',
    },
    {
      id: 'ft-6', stage: 'Final table · 6 left · you are 5th of 6', heroBB: 11, position: 'CO',
      hand: [c('K', 'spades'), c('Q', 'diamonds')],
      situation: 'Folds to you. The 6th-place stack has just 3bb and is in the BB next hand.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Min-raise/fold', evaluation: 'ok', explanation: 'Preserves laddering equity, but at 11bb a raise/fold with KQ throws away the hand\'s jam value if anyone plays back.' },
        { action: 'All-in', evaluation: 'best', explanation: 'KQo at 11bb is a clear jam even with a micro behind: the ladder matters, but blinding down to his level costs more than the pressure spot earns. Accumulate while HE ladders you nothing.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Waiting on a 3bb stack to bust while folding premium jam hands trades real equity for a tiny pay bump.' },
      ],
      keyLesson: 'Ladder awareness has limits: with a real hand, chips still beat waiting.',
    },
    {
      id: 'ft-7', stage: 'Final table · 3 left · you have 8bb, others 30bb+', heroBB: 8, position: 'BB',
      hand: [c('9', 'clubs'), c('9', 'hearts')],
      situation: 'The chip leader min-raises the button. SB folds.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'Nines at 8bb three-handed is the top of your range - the pay jump to 2nd cannot justify folding a hand this far ahead of a button min-raise range.' },
        { action: 'Call', evaluation: 'bad', explanation: 'Flatting nines at 8bb invites a flop with overcards where you guess for your tournament - jam while you are ahead.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding a premium pair as the short stack three-handed is surrender masquerading as ICM discipline.' },
      ],
      keyLesson: 'Short three-handed, premium pairs always play for stacks - the ladder is a tiebreaker, not a strategy.',
    },
  ],
};

// ============================================================================
// DIAMOND - The Winner's Edge (when the chart is wrong)
// ============================================================================
// Authored from real winning-player doctrine: push/fold charts are the
// floor, not the ceiling. The chart says "7bb SB, any ace: jam" - but the
// chart doesn't know WHO is in the big blind, how close the money is, or
// that the button comes to you next hand. Reading the CALLER and the
// CLOCK, not just your cards, is what separates ranked players from
// chart-followers.
const WINNERS_EDGE: TournamentChallenge = {
  id: 'winners-edge',
  title: "The Winner's Edge",
  tagline: 'The chart says jam. The winner asks: who calls?',
  description: 'Expert spots where the standard play is a mistake. Same cards, different caller, different clock - different answer. This is the layer that turns chart-followers into ranked players.',
  tier: 'diamond',
  rewardPkCash: 3.0,
  kind: 'gauntlet',
  concept: 'tournament-icm',
  proSeries: true,
  passThreshold: 6,
  steps: [
    {
      id: 'we-1', stage: 'Near the money · 23 left, 20 paid · 1000/2000 (250)', heroBB: 7, position: 'SB',
      hand: [c('A', 'clubs'), c('5', 'diamonds')],
      situation: 'Folds to you. The BB is the 60bb CHIP LEADER - he has already called two short-stack jams this level with A7o and K9s. Losing 7bb means nothing to him. You have the button next hand.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'The chart screams jam - and the chart is wrong HERE. His calling range is wide precisely because you can\'t hurt him, so your fold equity is nearly zero and A5o is dominated by half the aces he calls with. Folding costs 0.5bb and buys you the BUTTON next hand: better position, more targets, and three eliminations from the money. This exact discipline is worth real cashes.' },
        { action: 'All-in', evaluation: 'bad', explanation: 'A chart jam into the one player at the table who calls light. When he looks you up with A8o, you\'re drawing at three outs for your tournament three spots from the money - the classic "shoved because the chart said so" bust-out.' },
        { action: 'Limp', evaluation: 'bad', explanation: 'Limp-folding 7bb burns your orbit budget, and limp-calling puts it in worse than jamming would have.' },
      ],
      keyLesson: 'Fold equity lives in the CALLER, not the chart. Against a covering stack who calls light near the money, chart-jams become tournament-ending punts.',
    },
    {
      id: 'we-2', stage: 'Near the money · 23 left, 20 paid · 1000/2000 (250)', heroBB: 7, position: 'SB',
      hand: [c('A', 'clubs'), c('5', 'diamonds')],
      situation: 'Identical spot - but this time the BB is a 14bb regular, three spots from the money, who has folded to every jam this level protecting his stack.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'Same cards, opposite answer. THIS opponent is ICM-handcuffed: calling off 7bb of his 14 with a marginal hand near the money would be a disaster for him, so he folds everything but premiums. Your fold equity is enormous and the blinds+antes bump your stack 30% uncontested.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding here gives away the most profitable jam in tournament poker: a short-stack ace into a handcuffed medium stack. The we-1 fold was about HIS calling range, not about A5o.' },
        { action: 'Limp', evaluation: 'bad', explanation: 'Limping invites him to see a free flop that un-handcuffs him. The jam extracts full value from his ICM bind.' },
      ],
      keyLesson: 'The caller\'s handcuffs ARE your fold equity. Same hand, same stack, same seat - the opponent changes and jam becomes the only play.',
    },
    {
      id: 'we-3', stage: 'Mid-tournament · 80 left, 45 paid · 500/1000 (125)', heroBB: 7, position: 'SB',
      hand: [c('A', 'clubs'), c('5', 'diamonds')],
      situation: 'Same hand and stack a third time - but the money is miles away and the BB is an average 30bb stack.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'Far from the money there is no ICM distortion to exploit or fear - this is pure chip math, and the chart is RIGHT: A5o at 7bb blind-vs-blind is a clearly profitable jam. Knowing when the chart applies matters as much as knowing when it doesn\'t.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Over-applying the we-1 lesson. Without money pressure warping calling ranges, folding chart-jams just bleeds your short stack to death.' },
      ],
      keyLesson: 'The exceptions are near the money and at final tables. Deep in the field, trust the math - the winners\' folds are situational, not habitual.',
    },
    {
      id: 'we-4', stage: 'Stone bubble adjacent · 22 left, 20 paid · 1200/2400 (300)', heroBB: 8, position: 'UTG',
      hand: [c('K', 'spades'), c('9', 'spades')],
      situation: 'Two tables away, two players sit on under 2bb and post the big blind within two hands. Your own blinds don\'t reach you for another six hands.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Count both clocks: the micro-stacks are forced all-in within two hands, and YOUR blinds are six hands away. You can fold everything short of aces and almost certainly cash. K9s UTG is a jam on chip math and a clear fold on tournament math.' },
        { action: 'All-in', evaluation: 'bad', explanation: 'Risking your tournament two forced-bust-outs from the money, with six free hands in the bank, is donating a near-certain cash to the table.' },
      ],
      keyLesson: 'Two clocks always run: your blind clock and the shortest stacks\' bust clock. When theirs expires first, fold and cash.',
    },
    {
      id: 'we-5', stage: 'Near the money · 25 left, 20 paid · 800/1600 (200)', heroBB: 12, position: 'CO',
      hand: [c('6', 'clubs'), c('6', 'diamonds')],
      situation: 'Folds to you. The button is a 70bb chip leader who has snap-called the last three all-ins with hands as weak as QTo. The blinds are tight medium stacks.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Small pairs at jam depth are flips at best against a light caller - and he WILL call. A 55/45 for your tournament five from the money, offered by the one player your jam doesn\'t pressure, is a spot winners decline. Better spots come every orbit.' },
        { action: 'All-in', evaluation: 'ok', explanation: 'Not indefensible chip-wise - but you\'re choosing to flip for your tournament life near the money when a fold keeps every future edge alive. Ranked players pass thin flips they can\'t be forced into.' },
        { action: 'Min-raise/fold', evaluation: 'bad', explanation: 'Raise/folding a third of your stack with a hand that can\'t stand a jam is the worst of all lines against a station-y big stack.' },
      ],
      keyLesson: 'A jam that will definitely be called isn\'t a jam - it\'s a coin-flip proposal. Near the money, decline flips you don\'t need.',
    },
    {
      id: 'we-6', stage: 'Near the money · 24 left, 20 paid · 1000/2000 (250)', heroBB: 25, position: 'SB',
      hand: [c('K', 'hearts'), c('7', 'diamonds')],
      situation: 'Folds to you. The BB is an 18bb solid regular - four spots from the money, a stack that folding protects and calling risks.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'Flip the we-1 lesson around: now YOU cover, and HIS tournament is on the line. A solid reg folds almost everything here - calling off 18bb four from the money with less than a premium is a mistake he knows better than to make. Your cards barely matter; K7o is plenty.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Passing the most reliable pressure spot in the endgame. The covered medium stack near the money is the most foldable opponent in poker.' },
        { action: 'Raise 2.5x, fold to jam', evaluation: 'ok', explanation: 'Works against most regs, but the open-jam removes his rejam option entirely - against an 18bb stack, deny him the play.' },
      ],
      keyLesson: 'Every handcuff has two ends. When you\'re the one covering near the money, apply the pressure you just learned to fold against.',
    },
    {
      id: 'we-7', stage: 'Near the money · 23 left, 20 paid · 1000/2000 (250)', heroBB: 7, position: 'BTN',
      hand: [c('A', 'hearts'), c('8', 'spades')],
      situation: 'One orbit after folding A5o in the SB (we-1), you\'ve reached the button. Folds to you. Blinds are a 12bb reg and a 15bb reg, both playing tight toward the money.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'This is what the we-1 fold BOUGHT you: a better ace, in better position, against two handcuffed medium stacks instead of a free-rolling chip leader. Patience isn\'t passivity - it\'s moving the same chips into a spot with double the fold equity and a stronger hand when called.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding the spot you waited for defeats the entire point of the discipline. The fold in we-1 was rent paid for THIS jam.' },
        { action: 'Min-raise', evaluation: 'bad', explanation: 'At 7bb a min-raise commits you with no fold equity gained - ship it and put both handcuffed stacks to the test.' },
      ],
      keyLesson: 'Disciplined folds are investments. Collect on them: when the better spot arrives, the chips go in without hesitation.',
    },
    {
      id: 'we-8', stage: 'In the money · 18 left, all paid · 1200/2400 (300)', heroBB: 7, position: 'SB',
      hand: [c('A', 'clubs'), c('4', 'clubs')],
      situation: 'The bubble burst two hands ago. Folds to you. The BB is the same 60bb chip leader who calls everything.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'The we-1 spot, one bubble later - and now it\'s a jam. The min-cash is banked, the next pay jumps are small, and doubling THROUGH the guy who calls light is exactly how 7bb becomes a final-table stack. His loose calls flip from threat to opportunity the moment survival stops being worth more than chips.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Bubble discipline after the bubble is just burning equity. The reason to fold A5o in we-1 - protecting a near-certain cash - no longer exists.' },
      ],
      keyLesson: 'Every exploit has an expiry. The instant the money is locked, the light-caller you feared becomes the double-up you want.',
    },
  ],
};

// ============================================================================
// BRONZE - Early Stage Discipline (deep-stack fundamentals)
// ============================================================================
const EARLY_STAGE: TournamentChallenge = {
  id: 'early-stage-discipline',
  title: 'Early Stage Discipline',
  tagline: 'Deep stacks punish loose calls',
  description: 'At 100bb the blinds are tiny and the mistakes are huge. Learn which hands earn deep-stack money and which quietly bleed it.',
  tier: 'bronze',
  rewardPkCash: 0.5,
  kind: 'gauntlet',
  concept: 'preflop-ranges',
  passThreshold: 4,
  steps: [
    {
      id: 'es-1', stage: 'Early stage · 25/50 · 100bb deep', heroBB: 100, position: 'MP',
      hand: [c('A', 'clubs'), c('J', 'diamonds')],
      situation: 'A tight UTG player opens 3x at a full 9-handed table.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'AJo against a tight UTG open is the classic domination trap: when you hit your ace, he has AK/AQ; when you miss, you fold. Deep stacks amplify the reverse implied odds.' },
        { action: 'Call', evaluation: 'bad', explanation: 'Calling to make a dominated top pair is exactly how deep stacks disappear 60bb at a time.' },
        { action: '3-bet', evaluation: 'bad', explanation: '3-betting into the tightest range at the table with a dominated hand builds the pot for his premiums.' },
      ],
      keyLesson: 'Early and deep, offsuit broadways versus early-position strength are folds - domination costs stacks.',
    },
    {
      id: 'es-2', stage: 'Early stage · 25/50 · 100bb deep', heroBB: 100, position: 'BTN',
      hand: [c('5', 'hearts'), c('5', 'clubs')],
      situation: 'A loose MP player opens 3x. Both blinds are calling stations with full stacks.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'Small pairs are deep-stack gold: call 3bb to win 100 when you flop a set against players who cannot fold top pair. The implied odds are exactly why you have chips.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding set-mining odds this good with stations behind is declining the best deep-stack investment there is.' },
        { action: '3-bet', evaluation: 'bad', explanation: '3-betting 55 turns a hand that wants a cheap flop into a bloated pot you rarely win.' },
      ],
      keyLesson: 'Deep stacks fund set mining: small pairs want cheap flops and deep opponents.',
    },
    {
      id: 'es-3', stage: 'Early stage · 25/50 · 100bb deep', heroBB: 100, position: 'BB',
      hand: [c('K', 'hearts'), c('J', 'hearts')],
      situation: 'You defend KJs vs a CO open. Flop Kd 8s 4c. You check, he bets 2/3 pot, you call. Turn 2h. You check, he bets pot.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'Top pair good kicker is exactly a bluff-catcher here: call the turn, re-evaluate rivers. Raising folds out his bluffs and gets called only by hands that beat you.' },
        { action: 'Raise', evaluation: 'bad', explanation: 'Raising top-pair-decent-kicker deep-stacked isolates you against exactly the sets and AK that continue.' },
        { action: 'Fold', evaluation: 'ok', explanation: 'Tight but not absurd against a pot-sized turn barrel - though folding all your top pairs to aggression makes you easy to run over.' },
      ],
      keyLesson: 'Deep-stacked, one pair is a bluff-catcher, not a value-raising hand.',
    },
    {
      id: 'es-4', stage: 'Early stage · 50/100 · 80bb deep', heroBB: 80, position: 'CO',
      hand: [c('A', 'spades'), c('A', 'hearts')],
      situation: 'You open 2.5x, the button 3-bets to 8bb, blinds fold.',
      actionTo: 'Action on you.',
      options: [
        { action: '4-bet to 20bb', evaluation: 'best', explanation: 'Aces deep want a big pot NOW while ranges are widest. Flatting lets every flop shrink your edge; 4-betting builds the pot against his strongest continuing range.' },
        { action: 'Call', evaluation: 'ok', explanation: 'Trapping occasionally is fine, but as a default it invites multiway-feeling flops where aces shrink to one pair.' },
        { action: 'All-in', evaluation: 'bad', explanation: 'Open-jamming 80bb folds out everything you dominate - maximum hand strength, minimum extraction.' },
      ],
      keyLesson: 'Big pairs want big pots built early - grow them preflop, not after the flop scares everyone.',
    },
    {
      id: 'es-5', stage: 'Early stage · 50/100 · 75bb deep', heroBB: 75, position: 'BB',
      hand: [c('7', 'diamonds'), c('2', 'spades')],
      situation: 'Four players limp. You check your option. Flop 7c 7h 3d - trips! You check, an early limper bets, two callers.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Raise', evaluation: 'best', explanation: 'Three players have shown interest in a pot you quietly dominate. Raise now - limped-pot trips get paid by second-best hands that cannot imagine your 72.' },
        { action: 'Call', evaluation: 'ok', explanation: 'Slow-playing multiway lets draws in cheap; with this many opponents, building the pot immediately usually earns more.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'You have trips. Read the board again before clicking.' },
      ],
      keyLesson: 'Multiway pots reward fast-playing big hands - too many draws exist to trap.',
    },
    {
      id: 'es-6', stage: 'Early stage · 50/100 · 70bb deep', heroBB: 70, position: 'MP',
      hand: [c('Q', 'diamonds'), c('J', 'diamonds')],
      situation: 'You open QJs, a solid player calls on the button. Flop Ad Kd 5c - you flop a royal draw (flush draw + gutter). You c-bet, he raises 3x.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'Twelve outs to the nuts or near-nuts with deep stacks behind: calling keeps every bluff in his range and hides your hand for a massive payoff card.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding a nut flush draw plus gutter to one raise wastes one of the best drawing hands you will ever hold.' },
        { action: 'All-in', evaluation: 'ok', explanation: 'Semi-jamming has fold equity, but 70bb deep it converts a nut draw into a coin-flip when a call would keep his whole range paying you.' },
      ],
      keyLesson: 'Deep-stacked nut draws prefer calling - the implied odds on your hit dwarf the fold equity of a jam.',
    },
  ],
};

// ============================================================================
// BRONZE - Position Power (positional fundamentals under tournament stacks)
// ============================================================================
const POSITION_POWER: TournamentChallenge = {
  id: 'position-power',
  title: 'Position Power',
  tagline: 'The same cards play differently from every seat',
  description: 'Position decides which hands are profitable before the cards even matter. Learn to respect seat math in tournament stack depths.',
  tier: 'bronze',
  rewardPkCash: 0.5,
  kind: 'gauntlet',
  concept: 'position',
  passThreshold: 4,
  steps: [
    {
      id: 'pp-1', stage: 'Middle stage · 100/200 (25)', heroBB: 40, position: 'UTG',
      hand: [c('K', 'clubs'), c('J', 'spades')],
      situation: 'You are first to act, 9-handed, 40bb effective.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'KJo UTG at a full table has eight players behind who wake up with dominating hands. It is a top-30% hand that plays like a bottom-half one from this seat.' },
        { action: 'Raise', evaluation: 'bad', explanation: 'Opening KJo UTG invites 3-bets you must fold to and callers with position who dominate you.' },
        { action: 'Limp', evaluation: 'bad', explanation: 'Open-limping announces weakness and builds multiway pots where KJo makes dominated pairs.' },
      ],
      keyLesson: 'UTG at a full table plays a range you could show face-up: pairs, big aces, premium suited hands.',
    },
    {
      id: 'pp-2', stage: 'Middle stage · 100/200 (25)', heroBB: 40, position: 'BTN',
      hand: [c('K', 'clubs'), c('J', 'spades')],
      situation: 'Same hand, but folded to you on the button.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Raise', evaluation: 'best', explanation: 'The same KJo that folds UTG is a value open on the button: only two players left to beat, and you act last on every street.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding a top-20% button hand versus two random blind hands throws away the seat advantage.' },
        { action: 'Limp', evaluation: 'bad', explanation: 'Limping the button surrenders the initiative that makes the seat profitable.' },
      ],
      keyLesson: 'Seats, not cards, set the baseline - eight opponents make KJo trash, two make it a value hand.',
    },
    {
      id: 'pp-3', stage: 'Middle stage · 150/300 (40)', heroBB: 33, position: 'CO',
      hand: [c('8', 'spades'), c('8', 'diamonds')],
      situation: 'A solid UTG player opens 2.5x. You hold 88 in the cutoff.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'Medium pairs in position want to see a flop: set-mine cheap, and when the board comes low you can fight for pots his AK whiffed. 3-betting turns the hand into a guess.' },
        { action: '3-bet', evaluation: 'ok', explanation: 'Not terrible, but versus a solid UTG range a 3-bet mostly folds out worse and gets action from better.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding a pocket pair in position to a single open at 33bb is far too tight.' },
      ],
      keyLesson: 'Position converts marginal hands into playable ones - medium pairs love calling in position.',
    },
    {
      id: 'pp-4', stage: 'Middle stage · 150/300 (40)', heroBB: 30, position: 'SB',
      hand: [c('A', 'diamonds'), c('9', 'clubs')],
      situation: 'A loose cutoff opens 2.3x, button folds. You are in the small blind.',
      actionTo: 'Action on you.',
      options: [
        { action: '3-bet', evaluation: 'best', explanation: 'From the SB, out of position all hand, calling is the worst of both worlds. 3-betting A9o versus a loose opener wins now or takes the initiative - flatting just plays a guessing game with a dominated kicker.' },
        { action: 'Call', evaluation: 'bad', explanation: 'SB flats invite the BB in behind you and commit you to playing a mediocre ace out of position - the classic leak seat.' },
        { action: 'Fold', evaluation: 'ok', explanation: 'Respectable versus tighter openers, but against a loose CO the 3-bet prints.' },
      ],
      keyLesson: 'The small blind hates flatting: 3-bet or fold keeps you out of guessing games.',
    },
    {
      id: 'pp-5', stage: 'Late stage · 300/600 (75)', heroBB: 25, position: 'BTN',
      hand: [c('6', 'hearts'), c('5', 'hearts')],
      situation: 'A 28bb MP player opens 2.2x. You hold 65s on the button.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'In position with a hand that flops disguised monsters, at stack depths where winning his stack pays 12x the call - suited connectors on the button are how medium stacks become big ones.' },
        { action: 'Fold', evaluation: 'ok', explanation: 'Playable-tight at 25bb, but folding the button with this hand class passes up your best implied-odds seat.' },
        { action: '3-bet', evaluation: 'bad', explanation: '3-betting 65s at 25bb effective mostly gets you jammed on by hands you must fold to.' },
      ],
      keyLesson: 'The button is the implied-odds seat: speculative hands cash their promise there.',
    },
    {
      id: 'pp-6', stage: 'Late stage · 400/800 (100)', heroBB: 20, position: 'MP',
      hand: [c('A', 'clubs'), c('10', 'spades')],
      situation: 'You open ATo 2.2x, and the big blind - a tough aggressive player - calls. Flop Jc 8d 3s. He checks.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Check back', evaluation: 'best', explanation: 'Whiffed with a hand that has showdown value and two overs - checking keeps his check-raise off the table and your ace-high alive. C-betting every flop is the most exploited habit in poker.' },
        { action: 'C-bet 1/2 pot', evaluation: 'ok', explanation: 'A stab wins sometimes, but against a check-raiser on a board that smacks his defend range, checking protects your equity better.' },
        { action: 'C-bet pot', evaluation: 'bad', explanation: 'Overbetting a whiffed ace-high into a tough player is burning chips for pride.' },
      ],
      keyLesson: 'Position includes the right to check - in-position pot control is a weapon, not weakness.',
    },
  ],
};

// ============================================================================
// SILVER - Resteal & Squeeze (attacking opens and callers)
// ============================================================================
const RESTEAL_SQUEEZE: TournamentChallenge = {
  id: 'resteal-squeeze',
  title: 'Resteal & Squeeze',
  tagline: 'Their steal is your target',
  description: 'Late-position opens are wide - which makes them attackable. Learn when to 3-bet light, when to squeeze a caller, and when to leave it alone.',
  tier: 'silver',
  rewardPkCash: 1.0,
  kind: 'gauntlet',
  concept: 'steal-resteal',
  passThreshold: 5,
  steps: [
    {
      id: 'rs-1', stage: 'Late stage · 300/600 (75)', heroBB: 24, position: 'SB',
      hand: [c('A', 'spades'), c('4', 'spades')],
      situation: 'The button - stealing relentlessly - opens 2.2x. You are in the SB at 24bb.',
      actionTo: 'Action on you.',
      options: [
        { action: '3-bet to 7bb', evaluation: 'best', explanation: 'A4s is the perfect resteal: the ace blocks his continuing hands, the suit gives real equity when called, and his wide steal range folds to pressure constantly.' },
        { action: 'Call', evaluation: 'bad', explanation: 'Flatting a suited wheel ace out of position invites the classic dominated-or-whiffed guessing game.' },
        { action: 'Fold', evaluation: 'ok', explanation: 'Not a disaster, but folding blockers-plus-equity to the table\'s most frequent stealer leaves the best counter unused.' },
      ],
      keyLesson: 'Suited wheel aces are the premium resteal class: blockers when he folds, equity when he does not.',
    },
    {
      id: 'rs-2', stage: 'Late stage · 300/600 (75)', heroBB: 24, position: 'SB',
      hand: [c('A', 'spades'), c('4', 'spades')],
      situation: 'Same hand - but this time the open came from a tight UTG player at a full table.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Resteals attack wide ranges. A tight UTG open is not wide - your blocker barely dents a range of pairs and big aces, and your A4s is dominated when called.' },
        { action: '3-bet', evaluation: 'bad', explanation: 'Bluffing the one range at the table that does not fold is how resteal concepts get a bad name.' },
        { action: 'Call', evaluation: 'bad', explanation: 'Out of position versus a strong range with a dominated ace - every branch of this tree loses.' },
      ],
      keyLesson: 'The resteal targets the RANGE, not the hand - no wide range, no resteal.',
    },
    {
      id: 'rs-3', stage: 'Late stage · 400/800 (100)', heroBB: 22, position: 'BB',
      hand: [c('K', 'diamonds'), c('Q', 'clubs')],
      situation: 'The cutoff (30bb) opens 2.2x and the button (25bb, loose-passive) flat-calls.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Squeeze all-in', evaluation: 'best', explanation: 'The squeeze dream: an opener who folds to jams and a caller who telegraphed a capped range. KQo blocks their strongest continues, and 5.4bb of dead money pads every fold.' },
        { action: 'Call', evaluation: 'ok', explanation: 'Playable multiway, but KQo out of position multiway makes dominated pairs - the jam captures more EV from the dead money.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding KQo with this much dead money and two capped ranges is leaving a printing press unplugged.' },
      ],
      keyLesson: 'Open + flat-call = capped ranges + dead money: the squeeze\'s natural habitat.',
    },
    {
      id: 'rs-4', stage: 'Late stage · 400/800 (100)', heroBB: 45, position: 'BTN',
      hand: [c('9', 'diamonds'), c('9', 'clubs')],
      situation: 'A 40bb MP reg opens 2.3x, a 12bb short flat-calls in the CO (leaving 9.7bb behind).',
      actionTo: 'Action on you.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'The short\'s flat screams monster-trapping or desperation - either way HIS chips go in. Calling in position keeps the opener\'s range wide and lets you play a set-mine/value hybrid with the best seat.' },
        { action: 'Squeeze to 12bb', evaluation: 'ok', explanation: 'Reasonable, but it re-opens the action for the trapper and folds out the opener hands you beat.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding 99 in position at these depths is far too tight regardless of the action in front.' },
      ],
      keyLesson: 'Short-stack flats behind an open are traps more often than not - squeeze the deep money, not the desperate.',
    },
    {
      id: 'rs-5', stage: 'Late stage · 500/1000 (125)', heroBB: 18, position: 'BB',
      hand: [c('J', 'spades'), c('10', 'spades')],
      situation: 'The SB (35bb, aggressive) raises 3x into you after everyone folds.',
      actionTo: 'Action on you.',
      options: [
        { action: '3-bet all-in', evaluation: 'best', explanation: 'Blind-vs-blind, his 3x from the SB is wide and out of position. JTs at 18bb turns its excellent equity-when-called plus his high fold rate into a clearly profitable jam.' },
        { action: 'Call', evaluation: 'ok', explanation: 'JTs plays fine as a call, but you let him realize position-less equity cheaply when the jam pressures his whole wide range.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding a premium suited broadway to a wide blind-vs-blind raise gives the aggressor exactly what his 3x wanted.' },
      ],
      keyLesson: 'Blind-vs-blind raises are the widest in poker - meet them with your strongest resteal candidates.',
    },
    {
      id: 'rs-6', stage: 'Late stage · 500/1000 (125)', heroBB: 26, position: 'CO',
      hand: [c('7', 'hearts'), c('2', 'diamonds')],
      situation: 'A tilting player who just lost a huge pot opens 2x from MP for the third hand running.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Yes, he is opening light - but tilting players CALL light too, and 72o has no equity when he does. Resteal wider against him, just not with the worst hand in poker.' },
        { action: '3-bet', evaluation: 'bad', explanation: 'Bluffing a tilted player who has stopped folding is the most expensive read misapplication in poker.' },
      ],
      keyLesson: 'Attack tilters with hands, not air - tilt removes their fold button.',
    },
  ],
};

// ============================================================================
// SILVER - Ante Wars (how antes rewire the math)
// ============================================================================
const ANTE_WARS: TournamentChallenge = {
  id: 'ante-wars',
  title: 'Ante Wars',
  tagline: 'The pot got bigger - did your ranges?',
  description: 'Once antes kick in, every pot starts 60% bigger and every fold costs more. Recalibrate opens, defends, and jams for the ante era.',
  tier: 'silver',
  rewardPkCash: 1.0,
  kind: 'gauntlet',
  concept: 'preflop-ranges',
  passThreshold: 4,
  steps: [
    {
      id: 'aw-1', stage: 'Antes just began · 200/400 (50)', heroBB: 35, position: 'CO',
      hand: [c('J', 'diamonds'), c('8', 'diamonds')],
      situation: 'Antes started this level: the preflop pot is now 2.1bb instead of 1.5bb. Folds to you.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Min-raise', evaluation: 'best', explanation: 'Pre-ante, J8s in the CO is marginal. With 40% more dead money in every pot, it becomes a clear open - the reward for the same steal just grew.' },
        { action: 'Fold', evaluation: 'ok', explanation: 'The pre-ante instinct. Not awful, but leaving newly-profitable opens folded is how ante levels quietly eat stacks.' },
        { action: 'Open 3x', evaluation: 'bad', explanation: 'Antes reward MORE steals, not bigger ones - keep the sizing efficient.' },
      ],
      keyLesson: 'The moment antes begin, widen every opening range - the pot pays more for the same risk.',
    },
    {
      id: 'aw-2', stage: 'Ante era · 300/600 (75)', heroBB: 28, position: 'BB',
      hand: [c('8', 'clubs'), c('4', 'clubs')],
      situation: 'The button min-raises. With antes, you are getting 3.9:1 on the call.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: '84s pre-ante is a fold; at 3.9:1 it only needs ~20% pot share, and any suited hand clears that against a button range. The ante changed the answer.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'At almost 4:1 closing the action, folding suited hands is mathematically burning the ante subsidy.' },
        { action: '3-bet', evaluation: 'bad', explanation: 'Bluffing with a hand that profits as a call converts a plus-EV price into an unnecessary gamble.' },
      ],
      keyLesson: 'Antes turn the BB into a bargain bin - suited anything defends against late min-raises.',
    },
    {
      id: 'aw-3', stage: 'Ante era · 400/800 (100)', heroBB: 15, position: 'BTN',
      hand: [c('Q', 'diamonds'), c('8', 'spades')],
      situation: 'Folds to you at 15bb. The pot already holds 2.1bb of blinds and antes.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'Q8o is too weak to raise/fold and too good to open-fold with this much dead money: the 15bb button jam wins 2.1bb uncontested at a high frequency - a 14% stack bump per steal.' },
        { action: 'Fold', evaluation: 'ok', explanation: 'Close - but at exactly this stack and seat, passing on 2.1bb of dead money each orbit is the tighter-than-optimal path.' },
        { action: 'Min-raise', evaluation: 'bad', explanation: 'Raise/folding 15bb with Q8o bleeds the stack; raise/calling it stacks off dominated. The jam avoids both.' },
      ],
      keyLesson: 'Antes supercharge the 10-16bb jam: the dead money makes marginal shoves clearly profitable.',
    },
    {
      id: 'aw-4', stage: 'Ante era · 500/1000 (125)', heroBB: 40, position: 'UTG',
      hand: [c('K', 'spades'), c('9', 'spades')],
      situation: 'Antes in play, 8-handed. First to act.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Antes widen every seat a notch - but UTG stays fundamentally tight. K9s UTG still runs into seven ranges that dominate it; the ante subsidy does not cover that bill.' },
        { action: 'Raise', evaluation: 'bad', explanation: 'The "antes = open everything" overcorrection. Early seats gain the least because too many players remain behind.' },
      ],
      keyLesson: 'Antes widen ranges asymmetrically: a lot in late seats, barely at all up front.',
    },
    {
      id: 'aw-5', stage: 'Ante era · 600/1200 (150)', heroBB: 22, position: 'SB',
      hand: [c('K', 'hearts'), c('6', 'hearts')],
      situation: 'Folds to you against a BB who folds to steals 75% of the time. The pot holds 2.75bb with antes.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Raise 2.5x', evaluation: 'best', explanation: 'Versus a 75% folder with an ante-fattened pot, this steal shows immediate profit before your king-high suited hand even matters. The most reliable chip printer in the ante era.' },
        { action: 'Limp', evaluation: 'ok', explanation: 'Workable, but versus an over-folder you are declining the instant profit his fold frequency offers.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding K6s to a single over-folding opponent with a swollen pot is the definition of a leak.' },
      ],
      keyLesson: 'Ante-era steals against over-folders are profitable with any two - K6s is a luxury.',
    },
  ],
};

// ============================================================================
// GOLD - Satellite Survival (the most extreme ICM in poker)
// ============================================================================
const SATELLITE_SURVIVAL: TournamentChallenge = {
  id: 'satellite-survival',
  title: 'Satellite Survival',
  tagline: 'Where folding aces is correct',
  description: 'In a satellite every seat pays the same, so a chip gained is worth far less than a chip lost. Learn the most counterintuitive folds in poker.',
  tier: 'gold',
  rewardPkCash: 2.0,
  kind: 'gauntlet',
  concept: 'tournament-icm',
  passThreshold: 5,
  steps: [
    {
      id: 'sat-1', stage: 'Satellite bubble · 11 left, 10 seats · you: 2nd in chips', heroBB: 45, position: 'BB',
      hand: [c('A', 'spades'), c('A', 'hearts')],
      situation: 'The chip leader (only stack covering you) open-jams from the SB. Three stacks under 6bb are at the other table.',
      actionTo: 'Action on you: call for your tournament, or fold.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'The famous satellite fold. Winning gains you nothing - your seat was already near-certain. Losing the ~19% of the time aces get cracked costs the entire seat. No upside, real downside: fold aces.' },
        { action: 'Call', evaluation: 'bad', explanation: 'You risk a locked seat to win chips that cannot make your prize any bigger. In a satellite, this call has literally zero reward.' },
      ],
      keyLesson: 'When every seat pays the same and your seat is safe, no hand - not even aces - is worth a call for your tournament.',
    },
    {
      id: 'sat-2', stage: 'Satellite bubble · 11 left, 10 seats · you: 8bb, two stacks at 3bb', heroBB: 8, position: 'BTN',
      hand: [c('A', 'clubs'), c('K', 'clubs')],
      situation: 'Folds to you. The blinds both cover you. Two 3bb stacks at the other table pay blinds next hand.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'At 8bb you outlast both 3bb stacks by simply folding - they are forced all-in within two hands. AKs gains you a bigger stack you do not need; losing with it hands your seat to a 3bb stack.' },
        { action: 'All-in', evaluation: 'bad', explanation: 'Even at 70/30 you are drawing at nothing: your seat is already won on the blind clock. The 30% is pure seat-loss risk.' },
      ],
      keyLesson: 'In satellites, count blinds-until-bust for the shorter stacks - if they bust first on the clock, fold everything.',
    },
    {
      id: 'sat-3', stage: 'Satellite · 14 left, 10 seats · you: 4bb', heroBB: 4, position: 'CO',
      hand: [c('Q', 'spades'), c('9', 'spades')],
      situation: 'You are 12th of 14 in chips. Blinds hit you in three hands. Folds to you.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'The other side of satellite math: below the survival line, chips ARE your equity. At 4bb and 12th of 14, folding to the blinds guarantees elimination - jam your live cards while they still buy fold equity.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Preserving 4bb when you are outside the seats and the blinds arrive in three hands preserves nothing but the order you bust in.' },
      ],
      keyLesson: 'Satellite strategy flips at the survival line: above it, fold everything; below it, gamble hard.',
    },
    {
      id: 'sat-4', stage: 'Satellite bubble · 11 left, 10 seats · you: 30bb (3rd)', heroBB: 30, position: 'CO',
      hand: [c('K', 'diamonds'), c('K', 'clubs')],
      situation: 'A 6bb short stack open-jams UTG. You have kings; everyone behind you is covered by you.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'ok', explanation: 'The ultra-safe line, never a disaster on a satellite bubble - but versus a 6bb desperation jam that cannot hurt your seat, it gives up a nearly free elimination.' },
        { action: 'Call', evaluation: 'best', explanation: 'This risk is 6bb of a 30bb stack, not your seat - and busting the short brings the bubble to an instant end. Calling with a monster to END the bubble is satellite-correct.' },
        { action: 'All-in over the top', evaluation: 'bad', explanation: 'Re-jamming invites the covering stacks behind into a pot that was already heads-up won - flat-call and keep the guarantee.' },
      ],
      keyLesson: 'Safe stacks should call shorts with premiums: ending the bubble IS the prize.',
    },
    {
      id: 'sat-5', stage: 'Satellite · 12 left, 10 seats · you: 18bb (6th)', heroBB: 18, position: 'MP',
      hand: [c('A', 'diamonds'), c('Q', 'diamonds')],
      situation: 'A 20bb stack (5th place, the only close rival to your position) opens 2.5x. Two 5bb stacks sit behind.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'AQs is beautiful and irrelevant: tangling with the one stack that can leapfrog you, while two 5bb stacks are about to blind out, risks your near-locked seat for redundant chips.' },
        { action: '3-bet', evaluation: 'bad', explanation: 'Building a pot against your direct rival on a satellite bubble is exactly the confrontation the format punishes hardest.' },
        { action: 'Call', evaluation: 'bad', explanation: 'Even flatting commits you to playing a big pot with the wrong opponent at the wrong time.' },
      ],
      keyLesson: 'Near the seats, avoid the stacks that can hurt you - the 5bb stacks will do your work for you.',
    },
    {
      id: 'sat-6', stage: 'Satellite · 20 left, 10 seats · early-mid stage', heroBB: 25, position: 'BTN',
      hand: [c('9', 'clubs'), c('9', 'diamonds')],
      situation: 'Far from the bubble, a 24bb CO reg opens 2.2x. Standard stacks everywhere.',
      actionTo: 'Action on you.',
      options: [
        { action: '3-bet', evaluation: 'best', explanation: 'Away from the bubble, a satellite is just a tournament: 99 in position versus a late open plays fast for value. Bank the chips now that buy the folding luxury later.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Playing bubble-scared 10 spots from the bubble concedes the accumulation phase the fold-everything endgame depends on.' },
        { action: 'Call', evaluation: 'ok', explanation: 'Fine, but the satellite plan wants a stack that can fold through the bubble - build it while building is still correct.' },
      ],
      keyLesson: 'Satellite discipline is two games: normal poker early, extreme survival late. Do not mix up which one you are in.',
    },
  ],
};

// ============================================================================
// GOLD - Bounty Hunter (PKO math and coverage)
// ============================================================================
const BOUNTY_HUNTER: TournamentChallenge = {
  id: 'bounty-hunter',
  title: 'Bounty Hunter',
  tagline: 'Coverage changes everything',
  description: 'In progressive knockouts, the bounty on a covered player is instant cash - and it should bend your ranges. Learn when the bounty pays for the risk.',
  tier: 'gold',
  rewardPkCash: 2.0,
  kind: 'gauntlet',
  concept: 'tournament-icm',
  passThreshold: 5,
  steps: [
    {
      id: 'bh-1', stage: 'PKO middle stage · 200/400 (50)', heroBB: 45, position: 'BB',
      hand: [c('K', 'clubs'), c('9', 'diamonds')],
      situation: 'A 14bb player (you cover) open-jams the CO wearing a fat bounty worth ~8bb in cash. Folds to you.',
      actionTo: 'Action on you: call 13.5bb more or fold.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'Without the bounty K9o is a fold. Add ~8bb of bounty to the pot and you need far less equity - the knockout subsidy turns marginal calls into clear ones. This is THE core PKO adjustment.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding the same range you would in a regular tournament ignores the cash sitting on his head - the single biggest PKO leak.' },
      ],
      keyLesson: 'Convert the bounty to chips and add it to the pot - covered-player jams get called much wider.',
    },
    {
      id: 'bh-2', stage: 'PKO middle stage · 200/400 (50)', heroBB: 12, position: 'BB',
      hand: [c('K', 'clubs'), c('9', 'diamonds')],
      situation: 'Same jam, same bounty - but this time HE covers YOU (you have 12bb, he has 14bb).',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'The bounty only exists for the player who covers. You cannot win his bounty - but he can win yours. With the subsidy gone, K9o reverts to a plain fold.' },
        { action: 'Call', evaluation: 'bad', explanation: 'Calling wide WITHOUT coverage is the deadly PKO misapplication: you take the worst of it with no bounty upside.' },
      ],
      keyLesson: 'Coverage is binary: cover them and ranges widen; covered BY them and the bounty math vanishes.',
    },
    {
      id: 'bh-3', stage: 'PKO late stage · 500/1000 (125)', heroBB: 40, position: 'BTN',
      hand: [c('7', 'diamonds'), c('6', 'diamonds')],
      situation: 'Two short stacks (9bb and 7bb, both covered, both with big bounties) are in the blinds. Folds to you.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Min-raise', evaluation: 'best', explanation: 'Double coverage is the biggest range-widener in PKO poker: either blind may jam into you, and 76s can profitably call those jams with two bounties subsidizing the pot. Open relentlessly.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding the button over two covered bounties passes the format\'s most profitable recurring spot.' },
        { action: 'Open-jam 40bb', evaluation: 'bad', explanation: 'You want them jamming 7-9bb into you, not folding to 40 - keep the sizing small and the bounty bait dangling.' },
      ],
      keyLesson: 'Covering both blinds in a PKO is a license to attack - their jams pay you bounty equity.',
    },
    {
      id: 'bh-4', stage: 'PKO late stage · 500/1000 (125)', heroBB: 35, position: 'MP',
      hand: [c('A', 'spades'), c('J', 'spades')],
      situation: 'A 33bb reg (no meaningful coverage either way) opens 2.2x. Behind you: two 10bb bounty stacks.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'No bounty is in play against the near-equal opener, so normal rules apply: AJs plays great as a call. Flatting also keeps the two bounty shorts in jamming range behind you - inviting exactly the all-ins you want to call.' },
        { action: '3-bet', evaluation: 'ok', explanation: 'Standard in a regular MTT, but here it folds out the bounty stacks whose jams are your best PKO opportunity.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'AJs versus one MP open at 35bb is never a fold in any format.' },
      ],
      keyLesson: 'Shape the action toward bounty jams: sometimes the quiet call keeps the knockout window open.',
    },
    {
      id: 'bh-5', stage: 'PKO final two tables · 800/1600 (200)', heroBB: 25, position: 'CO',
      hand: [c('Q', 'clubs'), c('Q', 'hearts')],
      situation: 'A 24bb player with the tournament\'s LARGEST bounty (worth ~15bb cash) opens 2.5x. Stacks are effectively even.',
      actionTo: 'Action on you.',
      options: [
        { action: '3-bet all-in', evaluation: 'best', explanation: 'Queens plus a 15bb bounty upside on a near-covered stack: jamming maximizes the chance the whole prize goes in now. Even his snap-calls leave you flipping WITH a 15bb freeroll attached.' },
        { action: 'Call', evaluation: 'ok', explanation: 'Trapping keeps his bluffs in but risks a scare-card flop that talks him out of stacking off - with the mega-bounty up top, force the issue.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding queens with a giant bounty in view abandons both the chips and the cash.' },
      ],
      keyLesson: 'Big bounties justify big aggression - the freeroll attached to every showdown tilts close decisions toward jamming.',
    },
    {
      id: 'bh-6', stage: 'PKO early stage · 50/100 · 100bb deep', heroBB: 100, position: 'BB',
      hand: [c('Q', 'diamonds'), c('8', 'clubs')],
      situation: 'A 100bb UTG player raises 3x. You both hold starting bounties only (small).',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Early PKO bounties are small and stacks are deep - the bounty adds almost nothing per pot. Q8o versus UTG strength stays exactly the fold it always was.' },
        { action: 'Call', evaluation: 'bad', explanation: '"It\'s a bounty tournament" is not a reason to defend trash against strength when the bounty is worth 1/3 of a starting stack you would risk 100bb chasing.' },
      ],
      keyLesson: 'Bounty adjustments scale with bounty SIZE - early small bounties barely move ranges at all.',
    },
  ],
};

// ============================================================================
// DIAMOND - Stop & Go Academy (advanced short-stack weaponry)
// ============================================================================
const STOP_AND_GO: TournamentChallenge = {
  id: 'stop-and-go-academy',
  title: 'Stop & Go Academy',
  tagline: 'Advanced lines for desperate stacks',
  description: 'The stop-and-go, the limp-jam, the two-street shove: expert short-stack lines that squeeze fold equity out of stacks too small to threaten anyone preflop.',
  tier: 'diamond',
  rewardPkCash: 3.0,
  kind: 'gauntlet',
  concept: 'short-stack',
  passThreshold: 5,
  steps: [
    {
      id: 'sg-1', stage: 'Late stage · 500/1000 (125)', heroBB: 9, position: 'BB',
      hand: [c('8', 'hearts'), c('8', 'clubs')],
      situation: 'A 40bb button opens 2.2x. You are in the BB at 9bb.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Call, jam any flop (stop-and-go)', evaluation: 'best', explanation: 'The stop-and-go: preflop he calls your rejam getting a great price - but if you call and jam 7bb into the flop FIRST, he must fold every unpaired hand (two-thirds of flops miss him). Same chips, far more fold equity.' },
        { action: 'All-in preflop', evaluation: 'ok', explanation: 'Never bad with eights, but at this exact price he calls with everything - you surrender the fold-equity dimension the stop-and-go keeps.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'Folding a pair this healthy at 9bb in the BB needs no further comment.' },
      ],
      keyLesson: 'The stop-and-go moves your all-in to the street where HE has usually missed - post-flop fold equity is real, preflop it was zero.',
    },
    {
      id: 'sg-2', stage: 'Late stage · 500/1000 (125)', heroBB: 9, position: 'BB',
      hand: [c('A', 'clubs'), c('K', 'diamonds')],
      situation: 'Same spot - 40bb button opens 2.2x, you at 9bb - but now with AKo.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in preflop', evaluation: 'best', explanation: 'Flip the logic: AK WANTS the preflop call - you dominate his calling range and hold 40%+ against everything. Stop-and-go lines are for hands that fear the call; AK invites it.' },
        { action: 'Call, jam any flop', evaluation: 'ok', explanation: 'Not terrible, but when you brick the flop (two-thirds of the time) you jam with 6 outs into a hand that snap-calls - AK preferred its preflop equity.' },
        { action: 'Call, check-fold bad flops', evaluation: 'bad', explanation: 'Check-folding the best unpaired hand in poker after committing a quarter of your stack is the worst of every world.' },
      ],
      keyLesson: 'Stop-and-go with hands that fear calls; jam preflop with hands that welcome them.',
    },
    {
      id: 'sg-3', stage: 'Late stage · 600/1200 (150)', heroBB: 11, position: 'SB',
      hand: [c('A', 'hearts'), c('A', 'diamonds')],
      situation: 'Folds to you at 11bb in the SB. The BB is a hyper-aggressive 35bb stack who attacks limps and small raises relentlessly.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Limp, intending to jam over his raise', evaluation: 'best', explanation: 'The limp-jam: against a player who punishes limps, limping aces baits the raise, then the jam arrives with him already committed. Extracts far more than an open-jam he would simply fold to.' },
        { action: 'All-in', evaluation: 'ok', explanation: 'Safe but wasteful - open-jamming aces folds out the 100% of junk he would have attacked your limp with.' },
        { action: 'Min-raise', evaluation: 'ok', explanation: 'Workable, but the limp is the stronger trap against this specific opponent profile.' },
      ],
      keyLesson: 'The limp-jam weaponizes an aggressive opponent\'s instincts - let his aggression build your pot.',
    },
    {
      id: 'sg-4', stage: 'Late stage · 600/1200 (150)', heroBB: 6, position: 'BB',
      hand: [c('Q', 'hearts'), c('7', 'hearts')],
      situation: 'The SB (30bb) completes. You have 6bb and the option.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'A completing SB has capped his range - completions are weak by definition. Your 6bb jam over 2.5bb of dead money profits with any two; Q7s is a luxury. Punish the limp.' },
        { action: 'Check', evaluation: 'ok', explanation: 'A free flop is never awful, but at 6bb the dead-money jam against a capped range is the higher-EV line.' },
        { action: 'Raise to 3bb', evaluation: 'bad', explanation: 'A half-stack raise commits you with no fold equity gained - at 6bb the only raise is all of it.' },
      ],
      keyLesson: 'Micro-stacks feast on limps and completes - capped ranges fold to jams at enormous frequency.',
    },
    {
      id: 'sg-5', stage: 'Late stage · 800/1600 (200)', heroBB: 8, position: 'BTN',
      hand: [c('K', 'clubs'), c('6', 'clubs')],
      situation: 'A tight 25bb CO opens 2.2x. You are at 8bb on the button.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'A tight opener does not fold to an 8bb rejam - your fold equity is near zero, and K6s is crushed by his calling range. Advanced lines need ingredients this spot lacks.' },
        { action: 'All-in', evaluation: 'bad', explanation: 'Rejamming into a tight range with a dominated suited king is a punt dressed as aggression.' },
        { action: 'Call', evaluation: 'bad', explanation: 'Calling a quarter of your stack in position to play K-high poker leaves you no good flop plans.' },
      ],
      keyLesson: 'Every advanced line needs fold equity as fuel - versus tight ranges that never fold, plain discipline wins.',
    },
    {
      id: 'sg-6', stage: 'Late stage · 1000/2000 (250)', heroBB: 10, position: 'BB',
      hand: [c('J', 'diamonds'), c('9', 'diamonds')],
      situation: 'A 45bb chip leader min-raises the button for the fifth consecutive orbit. You call. Flop 9c 6h 2s - top pair. You are first to act with 8bb behind.',
      actionTo: 'Action on you.',
      options: [
        { action: 'All-in (donk jam)', evaluation: 'best', explanation: 'Top pair at 8bb: jam INTO him before he checks back. The lead-jam charges his floats and overcards immediately - checking risks a free card that beats you or a check-back that wins you nothing.' },
        { action: 'Check, call all-in', evaluation: 'ok', explanation: 'Gets the money in versus his stabs, but gives his check-back hands a free card against your vulnerable pair.' },
        { action: 'Check-fold', evaluation: 'bad', explanation: 'Check-folding top pair with 8bb behind against a serial stealer is surrendering a won pot.' },
      ],
      keyLesson: 'Short with top pair versus an aggressor: bet it yourself - protection beats trapping when one card can kill you.',
    },
  ],
};

// ============================================================================
// DIAMOND - Big Stack Bully (weaponizing a covering stack)
// ============================================================================
const BIG_STACK_BULLY: TournamentChallenge = {
  id: 'big-stack-bully',
  title: 'Big Stack Bully',
  tagline: 'Cover them, then squeeze',
  description: 'A covering stack in the late game is a pressure machine - if you know where the pressure works. Learn to bully correctly without spewing your lead.',
  tier: 'diamond',
  rewardPkCash: 3.0,
  kind: 'gauntlet',
  concept: 'tournament-icm',
  passThreshold: 4,
  steps: [
    {
      id: 'bb-1', stage: 'Late stage · 30 left, 27 paid · you: 60bb chip lead', heroBB: 60, position: 'BTN',
      hand: [c('J', 'clubs'), c('6', 'diamonds')],
      situation: 'Approaching the bubble. Blinds are 20bb and 24bb stacks - both would ladder into the money by folding.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Min-raise', evaluation: 'best', explanation: 'The covering stack near the bubble opens almost any two against medium stacks: their correct response to ICM is folding, which makes your J6o a profit machine. This is the spot the chip lead exists for.' },
        { action: 'Fold', evaluation: 'bad', explanation: 'The chip leader folding his button to two ICM-handcuffed blinds is refusing the format\'s clearest edge.' },
        { action: 'Open 3x', evaluation: 'ok', explanation: 'Works, but risks more than needed - the min-raise gets the same ICM folds at the lowest price.' },
      ],
      keyLesson: 'Near the bubble, the cover IS the hand - medium stacks must fold almost everything to you.',
    },
    {
      id: 'bb-2', stage: 'Late stage · 30 left, 27 paid · you: 60bb chip lead', heroBB: 60, position: 'BTN',
      hand: [c('J', 'clubs'), c('6', 'diamonds')],
      situation: 'Same spot - but now the blinds are 5bb and 6bb micro-stacks who must gamble soon anyway.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'Micro-stacks are the WRONG bullying targets: ICM barely binds them (they must gamble regardless), so they call/jam wide - and J6o has no showdown plan. Bully the stacks with something to lose.' },
        { action: 'Min-raise', evaluation: 'bad', explanation: 'Raising junk into desperate stacks that rejam any two playable cards converts your pressure edge into coin-flips.' },
      ],
      keyLesson: 'Pressure works on stacks with something to protect - micros call, mediums fold. Aim accordingly.',
    },
    {
      id: 'bb-3', stage: 'Final table · 6 left · you: 70bb, table avg 20bb', heroBB: 70, position: 'CO',
      hand: [c('K', 'spades'), c('10', 'diamonds')],
      situation: 'The 2nd stack (45bb, tough aggressive reg) 3-bets your open. Everyone else is 12-22bb.',
      actionTo: 'Action on you.',
      options: [
        { action: 'Fold', evaluation: 'best', explanation: 'The other big stack is the ONE player your cover means nothing against. KTo out of the equation, ego out of the equation: your lead prints against the mediums - protect it from the only real threat.' },
        { action: '4-bet', evaluation: 'bad', explanation: 'Escalating marginal hands into the one stack that can cripple you is how chip leads evaporate at final tables.' },
        { action: 'Call', evaluation: 'ok', explanation: 'Sees a flop in position, but playing a bloating pot with a dominated hand against the second stack is a needless leak in a printing situation.' },
      ],
      keyLesson: 'The bully\'s discipline: relentless against the covered, humble against the other big stack.',
    },
    {
      id: 'bb-4', stage: 'Final table · 5 left · you: 55bb', heroBB: 55, position: 'BB',
      hand: [c('8', 'spades'), c('3', 'hearts')],
      situation: 'The 14bb button (solid player, one jump from a big pay rise) min-raises. SB folds.',
      actionTo: 'Action on you: call 1bb, jam, or fold.',
      options: [
        { action: 'All-in', evaluation: 'best', explanation: 'His min-raise/fold range at 14bb under ladder pressure is enormous - a solid player raise/folding to preserve jump equity folds to your cover constantly. 83o is irrelevant; his ICM bind is the hand.' },
        { action: 'Fold', evaluation: 'ok', explanation: 'Never a catastrophe, but versus a min-raise from a ladder-bound 14bb stack, the cover-jam is among the highest-EV bullying moves that exist.' },
        { action: 'Call', evaluation: 'bad', explanation: 'Calling to play 83o postflop wastes the spot - the value is in his preflop fold, not your flop equity.' },
      ],
      keyLesson: 'Covered min-raisers under ladder pressure fold to jams at absurd rates - the ultimate bully spot.',
    },
    {
      id: 'bb-5', stage: 'Final table · 4 left · you: 50bb, shortest is 18bb', heroBB: 50, position: 'BTN',
      hand: [c('A', 'diamonds'), c('8', 'diamonds')],
      situation: 'You open 2.2x, and the 18bb SB - who has folded to your last six opens - jams. BB folds.',
      actionTo: 'Action on you: call 16bb more or fold.',
      options: [
        { action: 'Call', evaluation: 'best', explanation: 'Six folds then a jam is not a trap read - it is a player finally forced to fight back with his top range... which A8s still runs well against given 2.4:1 and the bully rebate: losing costs you a third of your lead, winning ends his resistance.' },
        { action: 'Fold', evaluation: 'ok', explanation: 'Foldable versus the tightest interpretations, but at this price the bully must pay off SOME resistance or the table learns his raises are paper.' },
      ],
      keyLesson: 'A bully who never calls the counterattack trains the table to jam him - budget for the payoffs.',
    },
  ],
};

// ============================================================================
// DIAMOND - Simulation challenges (phase 2: live multi-hand runner)
// ============================================================================
// These are LIVE MINI-GAMES: a real training table against real bots with a
// handicapped starting stack and a chip goal. Win the goal within the hand
// limit to claim the reward. This is the flagship "learn by playing" format.

const DOUBLE_UP: TournamentChallenge = {
  id: 'double-up-drill',
  title: 'Double-Up Drill',
  tagline: 'Turn 10bb into 20bb at a live table',
  description: 'A real table, real bots, and a 10 big blind stack. Double it within 15 hands using everything Push/Fold Precision taught you.',
  tier: 'bronze',
  rewardPkCash: 0.5,
  kind: 'simulation',
  concept: 'short-stack',
  passThreshold: 1,
  steps: [],
  simulation: {
    smallBlind: 100, bigBlind: 200,
    heroChips: 2000, botChips: 8000,
    botCount: 5, botDifficulty: 'medium',
    goalType: 'chips', goalChips: 4000, maxHands: 15,
    mission: 'Grow your 10bb stack to 20bb within 15 hands.',
    tips: [
      'At 10bb your main weapons are the all-in and the fold - avoid small raises you can\'t back up.',
      'Jam wider from late position: any pair, any ace, K9+ and suited broadways from the button.',
      'When a bot raises in front of you, rejam strong hands - never flat-call off a third of your stack.',
      'Every fold costs 1.5bb per orbit in blinds. Patience is good; paralysis is fatal.',
    ],
  },
};

const BLIND_ASSASSIN: TournamentChallenge = {
  id: 'blind-assassin',
  title: 'Blind Assassin',
  tagline: 'Win 5 pots before the flop',
  description: 'Live steal drill: win 5 pots PREFLOP within 20 hands. Nobody hands you chips - raise, pressure, and make the table fold.',
  tier: 'silver',
  rewardPkCash: 1.0,
  kind: 'simulation',
  concept: 'steal-resteal',
  passThreshold: 1,
  steps: [],
  simulation: {
    smallBlind: 100, bigBlind: 200,
    heroChips: 5000, botChips: 8000,
    botCount: 5, botDifficulty: 'medium',
    goalType: 'preflopWins', goalChips: 1, goalCount: 5, maxHands: 20,
    mission: 'Win 5 pots before the flop within 20 hands - only preflop wins count.',
    tips: [
      'Steal from the cutoff, button, and small blind - late position raises get the most folds.',
      'Min-raises are the cheapest steal: risk 2bb to win the blinds.',
      'Attack limpers with raises - a limped hand rarely calls a real raise.',
      'A called raise isn\'t a disaster - but only uncalled preflop wins count toward the goal.',
    ],
  },
};

const DEEP_WATER: TournamentChallenge = {
  id: 'deep-water',
  title: 'Deep Water',
  tagline: 'Grind 50bb into 65bb, no punting',
  description: 'Live deep-stack drill: turn 50bb into 65bb in 25 hands. No desperation jams here - value bets, position, and pot control do the work.',
  tier: 'silver',
  rewardPkCash: 1.0,
  kind: 'simulation',
  concept: 'preflop-ranges',
  passThreshold: 1,
  steps: [],
  simulation: {
    smallBlind: 50, bigBlind: 100,
    heroChips: 5000, botChips: 5000,
    botCount: 5, botDifficulty: 'medium',
    goalType: 'chips', goalChips: 6500, maxHands: 25,
    mission: 'Grow 50bb into 65bb within 25 hands using deep-stack fundamentals.',
    tips: [
      'Deep stacks reward position - play most of your pots from the button and cutoff.',
      'Value-bet relentlessly: bots call too much, so bet your good pairs three streets.',
      'Set-mine small pairs cheaply; one flopped set can win the whole challenge.',
      'Avoid bloated pots with one pair - keep them small and keep your stack safe.',
    ],
  },
};

const COMEBACK_KID: TournamentChallenge = {
  id: 'comeback-kid',
  title: 'Comeback Kid',
  tagline: 'From 3bb to 12bb - the great escape',
  description: 'Live short-stack rescue: 3 big blinds, hard bots, 20 hands to quadruple. Win a flip, then put your new stack to work.',
  tier: 'gold',
  rewardPkCash: 2.0,
  kind: 'simulation',
  concept: 'short-stack',
  passThreshold: 1,
  steps: [],
  simulation: {
    smallBlind: 100, bigBlind: 200,
    heroChips: 600, botChips: 8000,
    botCount: 5, botDifficulty: 'hard',
    goalType: 'chips', goalChips: 2400, maxHands: 20,
    mission: 'Rebuild 3bb into 12bb within 20 hands.',
    tips: [
      'At 3bb you need to double twice - take the first reasonable jam spot, don\'t wait for aces.',
      'Any ace, any pair, any two broadways: shove them from any seat.',
      'After the first double (6bb+), you can pick spots slightly more carefully.',
      'Jam INTO limpers and raises with real hands - dead money doubles you faster.',
    ],
  },
};

const ICE_VEINS: TournamentChallenge = {
  id: 'ice-veins',
  title: 'Ice Veins',
  tagline: 'Survive 20 hands against sharks',
  description: 'Live survival drill: 15bb at a table of hard bots. Finish 20 hands with your starting stack intact or better - fold discipline under fire.',
  tier: 'gold',
  rewardPkCash: 2.0,
  kind: 'simulation',
  concept: 'tournament-icm',
  passThreshold: 1,
  steps: [],
  simulation: {
    smallBlind: 100, bigBlind: 200,
    heroChips: 3000, botChips: 8000,
    botCount: 5, botDifficulty: 'hard',
    goalType: 'survive', goalChips: 3000, maxHands: 20,
    mission: 'Survive 20 hands and finish with at least your starting 15bb.',
    tips: [
      'This is bubble training: you must NOT bleed. Every orbit costs 1.5bb - steal it back once per orbit.',
      'Fold marginal hands to aggression - hard bots punish loose calls.',
      'Pick one good steal spot per orbit to stay level; you don\'t need to grow, just hold.',
      'If you win an early pot, you\'ve banked a cushion - tighten up and defend it.',
    ],
  },
};

const GIANT_SLAYER: TournamentChallenge = {
  id: 'giant-slayer',
  title: 'Giant Slayer',
  tagline: 'Double through the hard table',
  description: 'Live boss fight: 20bb against five hard bots. Reach 40bb within 25 hands - value, aggression, and picking the right fights.',
  tier: 'diamond',
  rewardPkCash: 3.0,
  kind: 'simulation',
  concept: 'steal-resteal',
  passThreshold: 1,
  steps: [],
  simulation: {
    smallBlind: 100, bigBlind: 200,
    heroChips: 4000, botChips: 8000,
    botCount: 5, botDifficulty: 'hard',
    goalType: 'chips', goalChips: 8000, maxHands: 25,
    mission: 'Double 20bb into 40bb within 25 hands against hard opposition.',
    tips: [
      'At 20bb the rejam is your best weapon - attack late-position raises with strong hands.',
      'Steal, but pick targets: note which bots fold their blinds and hit those seats hardest.',
      'One big double gets you most of the way - be willing to stack off with premium pairs and AK.',
      'Don\'t call raises with speculative hands at this depth; jam-or-fold keeps the pressure on them.',
    ],
  },
};

const SURGEON: TournamentChallenge = {
  id: 'short-stack-surgeon',
  title: 'Short Stack Surgeon',
  tagline: 'Turn 5bb into 15bb without punting',
  description: 'Live mini-game: start at 5bb and triple up using jams, rejams, and the min-raise weapon - against bots that punish mistakes.',
  tier: 'gold',
  rewardPkCash: 2.0,
  kind: 'simulation',
  concept: 'short-stack',
  passThreshold: 1,
  steps: [],
  simulation: {
    smallBlind: 100, bigBlind: 200,
    heroChips: 1000, botChips: 8000,
    botCount: 5, botDifficulty: 'hard',
    goalType: 'chips', goalChips: 3000, maxHands: 25,
    mission: 'Rebuild 5bb into 15bb within 25 hands.',
    tips: [
      'At 5bb, jam any ace, any pair, and strong suited hands - fold equity fades every orbit you wait.',
      'Position widens your jams: much looser from the button than under the gun.',
      'After a double, you\'re at ~10bb: keep jamming, but now respect early-position raises.',
      'The blinds are your deadline - count how many orbits you can survive and act before desperation.',
    ],
  },
};

const LAZARUS: TournamentChallenge = {
  id: 'lazarus-comeback',
  title: 'Lazarus: 0.5bb Comeback',
  tagline: 'Rebuild from half a big blind to 7bb',
  description: 'The ultimate live survival run: you start with HALF a big blind. Win the forced all-ins, pick every spot perfectly, and climb to 7 big blinds within 25 hands.',
  tier: 'diamond',
  rewardPkCash: 5.0,
  kind: 'simulation',
  concept: 'short-stack',
  passThreshold: 1,
  steps: [],
  simulation: {
    smallBlind: 200, bigBlind: 400,
    heroChips: 200, botChips: 12000,
    botCount: 5, botDifficulty: 'hard',
    goalType: 'chips', goalChips: 2800, maxHands: 25,
    mission: 'Climb from half a big blind to 7bb within 25 hands. Every all-in is for your tournament life.',
    tips: [
      'At 0.5bb you\'re all-in the moment chips go in - your only decision is WHICH hand to go with.',
      'Any ace, any king, any pair, any two suited connectors beats waiting - the blinds eat you in two hands.',
      'Early multiway all-ins are fine: triple-ups and quadruple-ups are how 0.5bb becomes 2bb+.',
      'Once you pass 3bb you have real fold equity again - switch to picking proper jam spots.',
    ],
  },
};

export const TOURNAMENT_CHALLENGES: TournamentChallenge[] = [
  STEAL_SCHOOL,
  EARLY_STAGE,
  POSITION_POWER,
  PUSH_FOLD,
  BB_FORTRESS,
  RESTEAL_SQUEEZE,
  ANTE_WARS,
  BUBBLE_TIGHTROPE,
  FINAL_TABLE,
  SATELLITE_SURVIVAL,
  BOUNTY_HUNTER,
  STOP_AND_GO,
  BIG_STACK_BULLY,
  WINNERS_EDGE,
  DOUBLE_UP,
  BLIND_ASSASSIN,
  DEEP_WATER,
  COMEBACK_KID,
  ICE_VEINS,
  GIANT_SLAYER,
  SURGEON,
  LAZARUS,
];

export const getChallengeById = (id: string): TournamentChallenge | undefined =>
  TOURNAMENT_CHALLENGES.find(ch => ch.id === id);

/** Score a finished run: best = 1 point, ok = 0.5, bad = 0. */
export const scoreRun = (
  challenge: TournamentChallenge,
  answers: Array<{ stepId: string; evaluation: StepOption['evaluation'] }>
): { points: number; passed: boolean } => {
  const points = answers.reduce(
    (s, a) => s + (a.evaluation === 'best' ? 1 : a.evaluation === 'ok' ? 0.5 : 0), 0);
  return { points, passed: points >= challenge.passThreshold };
};
